export function recommendationFamily(product){
  const type=String(product.type||product.product_type||product.category||'').toLowerCase(),title=String(product.title||product.product_title||'').toLowerCase(),value=type+' '+title;
  if(/glass|goblet|tumbler|margarita/.test(value))return 'glassware';
  if(/planter|flower pot/.test(value))return 'planters';
  if(/statue|figurine/.test(value))return 'figurines';
  return type||'other';
}
export function recommendationSeeds(products,limit=6){
  const families=new Map(),seen=new Set(),result=[];
  for(const product of products){const id=String(product.id||product.product_id);if(seen.has(id))continue;seen.add(id);const family=recommendationFamily(product);if(!families.has(family))families.set(family,[]);families.get(family).push({...product,id,family});}
  while(result.length<limit){let added=false;for(const rows of families.values())if(rows.length&&result.length<limit){result.push(rows.shift());added=true;}if(!added)break;}
  return result;
}
export function mixCartSuggestions(groups,inCart=[],getId=product=>product.id){
  const seen=new Set([...inCart].map(String)),chosen=[],counts=new Map(),families=new Map();
  for(const group of groups){if(!families.has(group.family))families.set(group.family,[]);families.get(group.family).push(group);}
  for(const [intent,limit] of [['complementary',2],['related',4]]){
    const queues=new Map([...families].map(([family,rows])=>[family,rows.map(row=>[...(row[intent]||[])])])),positions=new Map();
    while(chosen.length<limit){let added=false;
      for(const family of [...families.keys()].sort((a,b)=>(counts.get(a)||0)-(counts.get(b)||0))){
        const rows=queues.get(family),start=positions.get(family)||0;
        for(let step=0;step<rows.length;step++){const index=(start+step)%rows.length,queue=rows[index];let candidate;
          while(queue.length){const next=queue.shift(),id=String(getId(next));if(id&&!seen.has(id)){candidate=next;seen.add(id);break;}}
          if(candidate){chosen.push(candidate);counts.set(family,(counts.get(family)||0)+1);positions.set(family,(index+1)%rows.length);added=true;break;}
        }
        if(added)break;
      }
      if(!added)break;
    }
  }
  return chosen;
}

import { matchesProduct, qualifyOrder, wholesaleUnitCents, wholesaleEligibility, wholesaleStock, costWholesaleProduct } from './tth-wholesale-order-core.js?cartpolish=1020';
import { buildPurchaseGroups, planExactPieces } from './tth-wholesale-purchase-options-v1020.js';
import { wholesaleVolumeSavings } from './tth-wholesale-volume.js';
import { createShippingPolicyClient } from './tth-wholesale-shipping-policy-client.js?v=20261022';
import { createWholesaleActivity } from './tth-wholesale-activity.js?v=20261022';

// This module only manages a separate browser-side order. The checkout service must
// independently verify every variant, price, piece count, and eligibility rule.
const MAX_QUANTITY = 9999;
const PAGE_SIZE = 24;
const CACHE_AGE = 5 * 60 * 1000;
const LEGACY_QUOTE_KEY = 'th_wh_quote_list_v1';
const CHECKOUT_UNAVAILABLE = 'Online wholesale checkout is being prepared. Please contact us to complete this order.';
const CATALOG_PREWRITE_ERRORS = new Set(['catalog_changed', 'catalog_unverified', 'unavailable', 'insufficient_stock', 'stock_unverified', 'price_unverified', 'not_owned', 'minimum_not_met']);
const ADDRESS_FIELDS = ['firstName', 'lastName', 'company', 'address1', 'address2', 'city', 'provinceCode', 'zip', 'countryCode', 'phone'];
const ADDRESS_REVIEW_COPY = Object.freeze({
  message: 'The delivery estimate differs from your shipping address.',
  destinationLabel: 'Suggested state and ZIP:',
  reminder: 'Check your street and city before continuing.',
  accept: 'Use this state and ZIP',
  cancel: 'Keep my address',
  pending: 'Review the delivery destination below before continuing.'
});
const destinationZip5 = value => text(value, 20).match(/^(\d{5})(?:-\d{4})?$/)?.[1] || '';
const REQUIRED_ADDRESS_FIELDS = ['firstName', 'lastName', 'address1', 'city', 'provinceCode', 'zip', 'countryCode'];
const US_STATES = [['AL','Alabama'],['AK','Alaska'],['AZ','Arizona'],['AR','Arkansas'],['CA','California'],['CO','Colorado'],['CT','Connecticut'],['DE','Delaware'],['DC','District of Columbia'],['FL','Florida'],['GA','Georgia'],['HI','Hawaii'],['ID','Idaho'],['IL','Illinois'],['IN','Indiana'],['IA','Iowa'],['KS','Kansas'],['KY','Kentucky'],['LA','Louisiana'],['ME','Maine'],['MD','Maryland'],['MA','Massachusetts'],['MI','Michigan'],['MN','Minnesota'],['MS','Mississippi'],['MO','Missouri'],['MT','Montana'],['NE','Nebraska'],['NV','Nevada'],['NH','New Hampshire'],['NJ','New Jersey'],['NM','New Mexico'],['NY','New York'],['NC','North Carolina'],['ND','North Dakota'],['OH','Ohio'],['OK','Oklahoma'],['OR','Oregon'],['PA','Pennsylvania'],['RI','Rhode Island'],['SC','South Carolina'],['SD','South Dakota'],['TN','Tennessee'],['TX','Texas'],['UT','Utah'],['VT','Vermont'],['VA','Virginia'],['WA','Washington'],['WV','West Virginia'],['WI','Wisconsin'],['WY','Wyoming']];
const CONTIGUOUS_STATES = new Set(US_STATES.map(([code]) => code).filter(code => code !== 'AK' && code !== 'HI'));
const WESTERN_STATES = new Set(['AZ', 'CA', 'NV', 'UT']);
let manager = null;

function integer(value, allowZero = false) {
  const number = typeof value === 'number' || typeof value === 'string' && /^\d+$/.test(value) ? Number(value) : NaN;
  return Number.isSafeInteger(number) && (allowZero ? number >= 0 : number > 0) ? number : null;
}

function id(value, kind = 'ProductVariant') {
  const text = typeof value === 'number' && !Number.isSafeInteger(value) ? '' : String(value ?? '');
  return text.match(new RegExp(`^(?:gid://shopify/${kind}/)?([1-9]\\d{0,19})$`))?.[1] || '';
}

function shortTitle(value) { return String(value || '').split('|')[0].trim(); }
function text(value, max = 500) { return String(value ?? '').trim().slice(0, max); }
function parseJSON(raw) { try { return typeof raw === 'string' && raw.length < 8_000_000 ? JSON.parse(raw) : null; } catch { return null; } }
function activityEndpoint(value) {
  const safe=publicURL(value,true);
  if(!safe)return '';
  const url=new URL(safe);
  return url.pathname==='/apps/subscriber-list/wholesale/activity'&&!url.search&&!url.hash?safe:'';
}
function asRows(value) { return Array.isArray(value) ? value : Array.isArray(value?.lines) ? value.lines : Array.isArray(value?.items) ? value.items : []; }
function storageRead(storageName, key) { try { return window[storageName].getItem(key); } catch { return null; } }
function storageWrite(storageName, key, value) { try { window[storageName].setItem(key, value); return true; } catch { return false; } }
function storageRemove(storageName, key) { try { window[storageName].removeItem(key); } catch {} }

function publicURL(value, sameOrigin = false) {
  if (typeof value !== 'string' || !value.trim() || /[\\\r\n]/.test(value)) return '';
  try {
    const url = new URL(value, window.location.origin);
    if (url.protocol !== 'https:' && !(url.protocol === 'http:' && url.origin === window.location.origin)) return '';
    if (url.username || url.password || sameOrigin && url.origin !== window.location.origin) return '';
    return url.href;
  } catch { return ''; }
}

function element(tag, className, value) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (value !== undefined) node.textContent = value;
  return node;
}

function button(className, value, action) {
  const node = element('button', className, value);
  node.type = 'button';
  if (action) node.dataset.action = action;
  return node;
}

function image(src, className, alt = '') {
  const node = element('img', className);
  node.src = src;
  node.alt = alt;
  node.width = 144;
  node.height = 144;
  node.loading = 'lazy';
  node.decoding = 'async';
  return node;
}

function quantityInput(value, label) {
  const node = element('input');
  node.type = 'number';
  node.min = '1';
  node.max = String(MAX_QUANTITY);
  node.step = '1';
  node.value = String(value);
  node.required = true;
  node.setAttribute('aria-label', label);
  return node;
}

function normalizeIdentities(raw) {
  const merged = new Map();
  const conflicts = new Set();
  for (const row of asRows(raw).slice(0, 1000)) {
    const variantId = id(row?.variantId);
    const productId = id(row?.productId, 'Product');
    const quantity = integer(row?.quantity);
    if (!variantId || !productId || !quantity || quantity > MAX_QUANTITY || conflicts.has(variantId)) continue;
    const previous = merged.get(variantId);
    if (previous && previous.productId !== productId) { merged.delete(variantId); conflicts.add(variantId); continue; }
    // Storage describes one basket snapshot; repeated identities must not double it.
    merged.set(variantId, { variantId, productId, quantity: Math.max(quantity, previous?.quantity || 0) });
  }
  return [...merged.values()];
}

function randomKey() {
  return window.crypto?.randomUUID?.() || `wholesale-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function expiryTime(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value < 1_000_000_000_000 ? value * 1000 : value;
  if (typeof value === 'string') {
    if (/^\d+$/.test(value)) return expiryTime(Number(value));
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

function opaqueToken(value) { return typeof value === 'string' && /^[-a-zA-Z0-9_.~]{16,1024}$/.test(value) ? value : ''; }

function formField(form, name) {
  // Dawn associates its quantity control with the form through form="…", so it
  // can be outside the form subtree while still belonging to form.elements.
  return form?.elements?.namedItem?.(name) || form?.querySelector(`[name="${name}"]`) || null;
}

function showDialog(dialog, opener) {
  if (!dialog || dialog.open) return;
  dialog._tthOpener = opener || document.activeElement;
  try { dialog.showModal(); } catch { return; }
  dialog.querySelector('button, input, select, a[href]')?.focus();
}

function closeDialog(dialog) { if (dialog?.open) dialog.close(); }
function restoreFocus(dialog) {
  const opener = dialog?._tthOpener;
  const activeDialog = document.querySelector('dialog[open]');
  if (activeDialog && activeDialog !== dialog) return;
  if (opener?.isConnected && typeof opener.focus === 'function') opener.focus({ preventScroll: true });
}

const BrowserElement = typeof HTMLElement === 'undefined' ? class {} : HTMLElement;

export class TthWholesaleOrder extends BrowserElement {
  connectedCallback() {
    if (this.initialized || manager && manager !== this) return;
    this.initialized = true;
    manager = this;
    this.controller = new AbortController();
    this.products = new Map();
    this.variants = new Map();
    this.inlineProducts = new Map();
    this.builders = new Set();
    this.builderStates = new WeakMap();
    this.guides = new Map();
    this.lines = [];
    this.identities = [];
    this.catalogComplete = false;
    this.catalogLoading = false;
    this.catalogError = '';
    this.catalogRevalidationRequired = false;
    this.pendingLegacy = [];
    this.notice = '';
    this.idempotencyKey = '';
    this.attemptFingerprint = '';
    this.addressRevision = '';
    this.addressRevisionRestored = false;
    this.addressStepOpen = false;
    this.addressMessage = '';
    this.checkoutSession = null;
    this.quickAddProduct = null;
    const supplied = parseJSON(document.getElementById('TthWholesaleConfig')?.textContent || '') || {};
    this.config = {
      sourceURL: publicURL(supplied.sourceURL, true),
      storageKey: text(supplied.storageKey || 'tth_wholesale_order_v2', 150),
      legacyBuilderKey: text(supplied.legacyBuilderKey || '', 150),
      discountPercent: typeof supplied.discountPercent === 'number' && supplied.discountPercent >= 0 && supplied.discountPercent <= 100 ? supplied.discountPercent : 35,
      minimumSubtotalCents: integer(supplied.minimumSubtotalCents) || 15000,
      minimumPieces: integer(supplied.minimumPieces) || 5,
      maximumLines: Math.min(499, integer(supplied.maximumLines) || 499),
      currency: /^[A-Z]{3}$/.test(supplied.currency) ? supplied.currency : 'USD',
      baseCurrency: /^[A-Z]{3}$/.test(supplied.baseCurrency) ? supplied.baseCurrency : 'USD',
      checkoutEnabled: supplied.checkoutEnabled === true,
      requiredPolicyVersion: text(supplied.requiredPolicyVersion,120),
      activityEnabled: supplied.activityEnabled === true,
      activityEndpoint: activityEndpoint(supplied.activityEndpoint),
      addressReviewCopy: Object.fromEntries(Object.entries(ADDRESS_REVIEW_COPY).map(([key, fallback]) => [key, text(supplied.addressReviewCopy?.[key], 300) || fallback])),
      volumeDiscountEnabled: supplied.volumeDiscountEnabled === true,
      checkoutBaseEndpoint: publicURL(supplied.checkoutBaseEndpoint, true),
      checkoutURL: publicURL(supplied.checkoutURL || '/collections/wholesale?wholesale_checkout=1&pb=0', true)
    };
    this.cacheKey = `tth-wholesale-catalog-v10:${this.config.sourceURL}:${this.config.currency}`;
    this.dialog = this.querySelector('[data-order-dialog]');
    this.quickAddDialog = this.querySelector('[data-wholesale-quick-add-dialog]');
    this.addressStep = this.querySelector('[data-order-address-step]');
    this.addressForm = this.querySelector('[data-order-address-form]');
    this.configureCheckoutPage();
    this.installWholesaleActivity();
    this.populateAddressStates();
    this.installShippingPreview();
    this.installEvents();
    this.costPrices=new Map();
    this.discoverGuides();
    this.restoreOrder();
    this.restoreSelectedProducts();
    this.render();
    this.recordCartActivity();
    document.querySelectorAll('tth-wholesale-builder').forEach(builder => this.attachBuilder(builder));
    window.THWholesaleQuote = {
      open: () => this.open(),
      addItem: payload => this.addLegacyItem(payload),
      clear: () => this.clear(),
      setProductQuantity: async (productId, variantId, quantity) => {
        if (!this.products.has(String(productId))) await this.loadCatalog({selected:true});
        const product = this.products.get(String(productId)), group = product && this.groupForVariant(product, String(variantId));
        if (group?.mode === 'pieces') return this.setGroupQuantity(product.id, group.key, quantity);
        return this.setQuantity(String(variantId), quantity);
      },
      getQuantityLimit: (productId, variantId) => {
        const product = this.products.get(String(productId)), group = product && this.groupForVariant(product, String(variantId));
        return group?.mode === 'pieces' ? group.physicalStockCap : product ? this.variantInfo(product, product.variants.find(v=>v.id===String(variantId))).availableQuantity : null;
      },
      getCount: () => this.orderCount(),
      getSelection: (productId, variantId) => this.identities.find(line => line.productId === String(productId) && (!variantId || line.variantId === String(variantId))) || null,
      getSelectionStamp:productId=>JSON.stringify(this.identities.filter(line=>line.productId===String(productId)).map(line=>[line.variantId,line.quantity])),
      getCardView:async(productId,opener)=>{
        if(!this.products.has(String(productId)))await this.loadCardProduct(productId,opener);
        const product=this.products.get(String(productId));if(!product)return null;await this.loadCostPrices([product]);return this.renderCard(product,{choices:new Map()});
      },
      actOnCard:target=>{if(target.dataset.groupAdjust)this.adjustGroup(target.dataset.productId,target.dataset.groupKey,target.dataset.groupAdjust);else if(target.dataset.quickAddProduct)void this.quickAdd(target.dataset.quickAddProduct,target);},
      commitCardQuantity:input=>this.commitGroupQuantity(input),
      getCardPrice:async(productId,opener)=>{
        if(!this.products.has(String(productId)))await this.loadCardProduct(productId,opener);
        const product=this.products.get(String(productId));if(!product)return null;await this.loadCostPrices([product]);
        const floor=this.quantityPriceFloor(product);if(floor)return {cents:floor.plan.unitCents,unit:product.unitLabel||'piece',wholesale:floor.plan.lines.some(line=>line.spendEligible||line.eligible),from:floor.offer.pieces>1};
        const options=this.buyableVariants(product).map(variant=>this.variantInfo(product,variant)).filter(info=>info.unitCents>0).sort((a,b)=>a.unitCents-b.unitCents);const info=options[0];return info?{cents:info.unitCents,unit:product.unitLabel||'piece',wholesale:!!info.spendEligible,from:options.some(option=>option.unitCents!==info.unitCents)}:null;
      },
      addProduct: async (productId, variantId, opener, delta = 1) => {
        if (!this.products.has(String(productId))) await this.loadCardProduct(productId, opener);
        const product = this.products.get(String(productId));
        if (!product) { this.status('This item is not available for online wholesale ordering.'); return false; }
        await this.loadCostPrices([product]);
        const variant = product.variants.find(item => item.id === String(variantId));
        const group = variant && this.groupForVariant(product, variant.id);
        if (group?.mode === 'pieces') return this.setGroupQuantity(product.id, group.key, this.groupQuantity(group) + delta);
        if (variant && delta > 0) return this.add(product, variant, delta, { open: false });
        if (variant && delta < 0) { this.adjustLine(variant.id, 'decrease'); return true; }
        return this.quickAdd(productId, opener);
      }
    };
    // Ordinary retail visits with no wholesale selections do not load the catalog.
    if(this.pendingLegacy.length)void this.loadCatalog();else if(this.identities.length)void this.refreshSavedSelections();
    this.startGuideWatcher();
    if (this.checkoutPage || this.identities.length || this.builders.size) void this.ensureShippingPolicy();
  }

  disconnectedCallback() {
    this.controller?.abort();
    try { this.wholesaleActivity?.destroy(); } catch {}
    try { this.activityShippingObserver?.disconnect(); } catch {}
    this.activityShippingObserver=null;
    this.activityQuoteNodes=null;
    this.wholesaleActivity=null;
    this.shippingPolicy?.dispose();
    this.shippingPolicy = null;
    this.shippingPolicyRefresh = null;
    window.clearInterval(this.guideTimer);
    this.guideTimer = null;
    if (manager === this) manager = null;
    this.initialized = false;
  }

  installEvents() {
    const signal = this.controller.signal;
    this.addEventListener('click', event => {
      if (event.target.closest('[data-order-fab]')) this.open(event.target.closest('[data-order-fab]'));
      if (event.target.closest('[data-order-close]')) closeDialog(this.dialog);
      if (event.target.closest('[data-order-purchase]')) void this.purchase();
      if (event.target.closest('[data-wholesale-quick-add-close]')) closeDialog(this.quickAddDialog);
      const quickAdd = event.target.closest('[data-quick-add-product]');
      if (quickAdd) this.quickAdd(quickAdd.dataset.quickAddProduct, quickAdd);
      const step = event.target.closest('[data-quick-quantity-step]');
      if (step) {
        const input = step.parentElement.querySelector('input');
        input.value = String(Math.max(1, Math.min(Number(input.max) || MAX_QUANTITY, (integer(input.value) || 1) + Number(step.dataset.quickQuantityStep))));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      if (event.target.closest('[data-quick-add-submit]')) this.submitQuickAdd();
      const groupAdjustment = event.target.closest('[data-group-adjust]');
      if (groupAdjustment) this.adjustGroup(groupAdjustment.dataset.productId, groupAdjustment.dataset.groupKey, groupAdjustment.dataset.groupAdjust);
      const removeGroup = event.target.closest('[data-remove-group]');
      if (removeGroup) this.setGroupQuantity(removeGroup.dataset.productId, removeGroup.dataset.removeGroup, 0);
      const adjustment = event.target.closest('[data-line-adjust]');
      if (adjustment) this.adjustLine(adjustment.dataset.variantId, adjustment.dataset.lineAdjust);
      const remove = event.target.closest('[data-action="remove-line"]');
      if (remove) this.setQuantity(remove.dataset.variantId, 0);
      if (event.target === this.dialog) {
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog(this.dialog);
      }
    }, { signal });
    this.addEventListener('change', event => {
      if (event.target.closest('[data-order-address-form]')) this.addressDestinationEdited(event);
      if (event.target.matches('[data-quick-add-variant]')) { this.renderQuickAdd(event.target.value); return; }
      if (event.target.matches('[data-quick-add-group]')) { this.renderPieceChooser(event.target.value); return; }
      if (event.target.matches('[data-group-piece-quantity]')) this.commitGroupQuantity(event.target);
      if (event.target.matches('[data-line-quantity]')) this.commitLineQuantity(event.target);
    }, { signal });
    // Some autofill/browser tooling updates number values without dispatching a
    // committed change. Validate on blur as well, without interrupting typing.
    this.addEventListener('focusout', event => {
      if (event.target.matches('[data-line-quantity]')) this.commitLineQuantity(event.target);
      if (event.target.matches('[data-group-piece-quantity]')) this.commitGroupQuantity(event.target);
    }, { signal });
    this.addEventListener('input', event => {
      if (event.target.matches('[data-quick-add-piece-quantity]')) {
        const maximum = Number(event.target.max), value = Number(event.target.value);
        if (value > maximum) { event.target.value = String(maximum); this.toast('Only ' + maximum + ' in stock.'); }
        this.renderPiecePreview(integer(event.target.value));
      }
      if (event.target.matches('[data-quick-add-quantity]')) { const max=Number(event.target.max); if(Number(event.target.value)>max){event.target.value=String(max);this.toast('Only '+max+' in stock.');} this.renderQuickMinimum(); }
    }, { signal });
    this.addressForm?.addEventListener('input', event => this.addressDestinationEdited(event), { signal });
    this.addressForm?.addEventListener('change', event => this.addressDestinationEdited(event), { signal });
    this.addressStep?.querySelector('[data-order-address-back]')?.addEventListener('click', event => {
      event.preventDefault();
      this.returnToBuilder();
    }, { signal });
    this.addressForm?.addEventListener('submit', event => {
      event.preventDefault();
      void this.submitAddress();
    }, { signal });
    this.dialog?.addEventListener('close', () => restoreFocus(this.dialog), { signal });
    this.quickAddDialog?.addEventListener('close', () => {
      this.quickAddProduct = null;
      if (this.dialog?.open) this.dialog.querySelector('[data-order-close]')?.focus({ preventScroll: true });
      else restoreFocus(this.quickAddDialog);
    }, { signal });
    window.addEventListener('storage', event => {
      if (event.key !== this.config.storageKey) return;
      this.identities = normalizeIdentities(parseJSON(event.newValue));
      this.pendingLegacy = [];
      this.notice = 'Your wholesale order was updated in another tab.';
      this.render();
      if (this.hasUnresolvedLines()) void this.loadCatalog();
    }, { signal });
    document.addEventListener('change', () => this.refreshGuides(), { signal });
    document.addEventListener('variant:change', () => this.refreshGuides(), { signal });
    document.addEventListener('product:variant-change', () => this.refreshGuides(), { signal });
    document.addEventListener('shopify:section:load', () => {
      this.discoverGuides();
      document.querySelectorAll('tth-wholesale-builder').forEach(builder => this.attachBuilder(builder));
    }, { signal });
  }

  money(amount, currency = this.config.currency) {
    if (!Number.isFinite(amount)) return 'Price pending';
    try { return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(amount / 100); }
    catch { return `${currency} ${(amount / 100).toFixed(2)}`; }
  }

  currencySupported() { return this.config.currency === this.config.baseCurrency; }
  addressNode(selector) { return this.addressStep?.querySelector(selector) || this.querySelector(selector); }

  configureCheckoutPage() {
    let mode = false;
    try {
      const current = new URL(window.location.href || '/', window.location.origin);
      mode = current.pathname === new URL(this.config.checkoutURL).pathname && current.searchParams.get('wholesale_checkout') === '1';
    } catch {}
    this.checkoutPage = mode;
    this.checkoutMount = mode ? document.querySelector('[data-wholesale-checkout-mount]') : null;
    if (!mode) return;
    document.body?.classList.add('tth-wholesale-checkout-mode');
    if (!this.checkoutMount || !this.addressStep) return;
    const placeholder = this.checkoutMount.querySelector('[data-wholesale-checkout-heading]');
    if (placeholder && placeholder.tagName !== 'H1') {
      const heading = element('h1', placeholder.className, placeholder.textContent);
      heading.id = placeholder.id;
      placeholder.replaceWith(heading);
    }
    // Marketing remains the catalog's sole H1 in normal mode. The dedicated
    // checkout has its own H1, so remove that catalog subtree entirely here.
    document.querySelectorAll('tth-wholesale-builder').forEach(builder => builder.remove());
    this.checkoutMount.hidden = false;
    (this.checkoutMount.querySelector('.tth-checkout-page__layout') || this.checkoutMount).append(this.addressStep);
    this.checkoutSummary = this.checkoutMount.querySelector('[data-wholesale-checkout-summary]');
    const delivery=this.querySelector('[data-order-delivery-slot]');if(delivery)this.checkoutSummary?.append(delivery);
    this.checkoutItems = this.checkoutMount.querySelector('[data-order-checkout-items]');
    this.checkoutTotals = this.checkoutMount.querySelector('[data-order-checkout-totals]');
    if (this.checkoutSummary && !this.checkoutItems) {
      this.checkoutItems = element('ul', 'tth-wo__checkout-items');
      this.checkoutSummary.append(this.checkoutItems);
    }
    if (this.checkoutSummary && !this.checkoutTotals) {
      this.checkoutTotals = element('div', 'tth-wo__checkout-totals');
      this.checkoutSummary.append(this.checkoutTotals);
    }
    // Moving the form out of the closed dialog prevents inert modal content.
    // Its direct form listeners survive the move; no address is persisted.
    this.addressStepOpen = true;
  }

  returnToBuilder() {
    const destination = new URL(this.config.checkoutURL, window.location.origin);
    destination.searchParams.delete('wholesale_checkout');
    window.location.assign(destination.href);
  }
  status(message) {
    this.notice = message;
    const node = this.querySelector('[data-order-status]');
    if (node) node.textContent = message;
  }

  restoreOrder() {
    const saved = storageRead('localStorage', this.config.storageKey);
    if (saved !== null) {
      this.identities = normalizeIdentities(parseJSON(saved));
      if (saved && parseJSON(saved) === null) this.notice = 'The saved wholesale order could not be read. Please add your items again.';
      return;
    }
    const legacyQuote = asRows(parseJSON(storageRead('localStorage', LEGACY_QUOTE_KEY)));
    const legacyBuilder = this.config.legacyBuilderKey ? asRows(parseJSON(storageRead('sessionStorage', this.config.legacyBuilderKey))) : [];
    this.pendingLegacy = [
      ...legacyQuote.map(row => ({ ...row, _legacySource: 'quote' })),
      ...legacyBuilder.map(row => ({ ...row, _legacySource: 'builder' }))
    ].slice(0, 1000);
    if (this.pendingLegacy.length) this.notice = 'Checking your previous wholesale selections against the current catalog…';
  }

  restoreCostPriceCache(){
    this.costPrices=new Map();
    const saved=parseJSON(storageRead('sessionStorage',this.config.storageKey+':selling-prices-v1'));
    if(saved?.currency!==this.config.currency||!Array.isArray(saved.rows)||Date.now()<saved.savedAt||Date.now()-saved.savedAt>30*60*1000)return;
    for(const [key,cents] of saved.rows)if(id(key)&&integer(cents))this.costPrices.set(key,cents);
  }
  cacheSelectedProducts(){
    if(!Array.isArray(this.identities))return;
    const selected=new Set(this.identities.map(line=>line.productId));
    const previous=parseJSON(storageRead('localStorage',this.config.storageKey+':selected-products-v2'));
    const merged=new Map((Array.isArray(previous?.products)?previous.products:[]).filter(product=>selected.has(product.id)).map(product=>[product.id,product]));
    for(const product of this.products.values())if(selected.has(product.id))merged.set(product.id,product);
    const products=[...merged.values()];
    storageWrite('localStorage',this.config.storageKey+':selected-products-v2',JSON.stringify({currency:this.config.currency,savedAt:Date.now(),products}));
  }
  restoreSelectedProducts(){
    const saved=parseJSON(storageRead('localStorage',this.config.storageKey+':selected-products-v2'));
    if(saved?.currency!==this.config.currency||!Array.isArray(saved.products)||Date.now()<saved.savedAt)return;
    const selected=new Set(this.identities.map(line=>line.productId));
    for(const product of saved.products)if(selected.has(product.id)&&!this.products.has(product.id))this.registerProduct(product);
  }
  async refreshSavedSelections(){
    if(this.savedSelectionsPromise)return this.savedSelectionsPromise;
    this.catalogRevalidationRequired=true;
    this.savedSelectionsPromise=(async()=>{
      try{
        const ids=[...new Set(this.identities.map(line=>line.productId))];
        for(let offset=0;offset<ids.length;offset+=4){
          const results=await Promise.all(ids.slice(offset,offset+4).map(async productId=>{
            if(this.inlineProducts.has(productId))return this.products.get(productId);
            const previous=this.products.get(productId);if(!previous?.url)return null;
            const url=new URL(previous.url,location.origin);url.search='';url.searchParams.set('view','tth-wholesale-data');
            const controller=new AbortController(),timer=setTimeout(()=>controller.abort(),12000);
            try{const response=await fetch(url.href,{credentials:'same-origin',signal:controller.signal});if(!response.ok)return null;const data=await response.json();if(String(data.product?.id)!==productId)return null;return this.registerProduct(data.product);}catch{return null;}finally{clearTimeout(timer);}
          }));
          await this.loadCostPrices(results.filter(Boolean));
          if(results.some(product=>!product)){await this.loadCatalog({selected:true});this.catalogRevalidationRequired=this.hasUnresolvedLines();this.cacheSelectedProducts();return;}
          this.hydrate();this.render();
        }
        this.catalogRevalidationRequired=this.hasUnresolvedLines();
        this.cacheSelectedProducts();
      }finally{this.savedSelectionsPromise=null;this.render();}
    })();return this.savedSelectionsPromise;
  }

  persist() {
    this.cacheSelectedProducts();
    const identities = this.identities.map(({ productId, variantId, quantity }) => ({ productId, variantId, quantity }));
    if (!storageWrite('localStorage', this.config.storageKey, JSON.stringify(identities))) {
      this.notice = 'Your order is available in this tab, but this browser could not save it for your next visit.';
    }
    this.scheduleCartActivity();
  }

  installWholesaleActivity() {
    this.wholesaleActivity=null;
    if(this.config.activityEnabled!==true||!this.config.activityEndpoint)return;
    try {
      this.wholesaleActivity=createWholesaleActivity({enabled:true,endpoint:this.config.activityEndpoint,
        storageKey:text(this.config.storageKey,75)+':activity',origin:window.location.origin,
        includePostalCode:false,getHeaders:()=>this.wholesaleActivityHeaders()});
    } catch { /* Optional diagnostics never affect the order. */ }
  }

  activityDetail(extra={}) {
    if(!Array.isArray(this.identities)||!this.identities.length)return null;
    const lines=this.identities.map(row=>{
      const current=this.lines?.find(line=>line.variantId===row.variantId);
      return {variantId:String(row.variantId),productId:String(row.productId),quantity:row.quantity,
        ...(Number.isSafeInteger(current?.unitCents)&&current.unitCents>=0?{unitCents:current.unitCents}:{})};
    });
    let summary;
    try { summary=this.qualification(); } catch { /* IDs and quantities are sufficient during hydration. */ }
    const detail={lines};
    if(Number.isSafeInteger(summary?.subtotalCents)&&summary.subtotalCents>=0)detail.qualifyingSubtotalCents=summary.subtotalCents;
    if(Number.isSafeInteger(summary?.pieces)&&summary.pieces>=0)detail.eligiblePieces=summary.pieces;
    for(const key of ['shippingCents','freeThresholdCents'])if(Number.isSafeInteger(extra[key])&&extra[key]>=0)detail[key]=extra[key];
    if(US_STATES.some(([code])=>code===extra.stateCode))detail.stateCode=extra.stateCode;
    if(['address_incomplete','destination_review_pending','minimum_not_met','shipping_unconfirmed','basket_changed','network_error'].includes(extra.reasonCode))detail.reasonCode=extra.reasonCode;
    if(Number.isInteger(extra.httpStatus)&&extra.httpStatus>=100&&extra.httpStatus<=599)detail.httpStatus=extra.httpStatus;
    return detail;
  }

  recordWholesaleActivity(stage,extra={}) {
    try {
      if(!this.wholesaleActivity)return false;
      const detail=this.activityDetail(extra);
      return !!detail&&this.wholesaleActivity.record(stage,detail)===true;
    } catch { return false; }
  }

  recordCartActivity() {
    try {
      if(!this.wholesaleActivity)return;
      const detail=this.activityDetail();
      if(!detail){
        const cleared=!!this.activityCartSnapshot;
        this.activityCartSnapshot='';this.activityShippingSnapshot='';
        if(cleared){
          try { this.wholesaleActivity.record('cart_cleared',{lines:[],qualifyingSubtotalCents:0,eligiblePieces:0}); } catch {}
          try { this.wholesaleActivity.reset(); } catch {}
          this.flushWholesaleActivity();
        }
        return;
      }
      const snapshot=JSON.stringify(detail);
      if(snapshot===this.activityCartSnapshot)return;
      this.activityCartSnapshot=snapshot;
      this.wholesaleActivity.record('cart_updated',detail);
    } catch { /* Neither browser storage nor tracking is required for wholesale. */ }
  }

  scheduleCartActivity() {
    if(this.activityCartScheduled||!this.wholesaleActivity)return;
    this.activityCartScheduled=true;
    void Promise.resolve().then(()=>{this.activityCartScheduled=false;if(!this.controller.signal.aborted)this.recordCartActivity();}).catch(()=>{});
  }

  recordVisibleShipping(node,quote,summary) {
    try {
      if(!quote?.supported){this.activityQuoteNodes?.delete(node);this.activityShippingObserver?.unobserve(node);return;}
      if(!this.wholesaleActivity)return;
      this.activityQuoteNodes ||= new WeakMap();
      this.activityQuoteNodes.set(node,{shippingCents:quote.amountCents,freeThresholdCents:quote.freeThresholdCents,
        stateCode:text(formField(this.addressForm,'provinceCode')?.value,2).toUpperCase()||this.deliveryState});
      if(typeof window.IntersectionObserver==='function'){
        this.activityShippingObserver ||= new window.IntersectionObserver(entries=>{
          for(const entry of entries)if(entry.isIntersecting&&entry.intersectionRatio>0)this.recordShippingIntersection(entry.target,true);
        },{threshold:0.01});
        this.activityShippingObserver.observe(node);
      }else this.recordShippingIntersection(node,false);
    } catch { /* Pure calculations and below-fold shipping do not emit views. */ }
  }

  recordShippingIntersection(node,intersecting) {
    try {
      const extra=this.activityQuoteNodes?.get(node);
      if(!extra||!node.isConnected||node.hidden||node.closest('[hidden]')||node.closest('dialog')&&!node.closest('dialog').open
        ||typeof node.checkVisibility==='function'&&!node.checkVisibility())return;
      if(!intersecting){
        const rect=node.getBoundingClientRect?.(),height=window.innerHeight,width=window.innerWidth;
        if(!rect||![rect.top,rect.bottom,rect.left,rect.right,height,width].every(Number.isFinite)||height<=0||width<=0
          ||rect.bottom<=0||rect.top>=height||rect.right<=0||rect.left>=width||rect.bottom<=rect.top||rect.right<=rect.left)return;
      }
      const snapshot=JSON.stringify(this.activityDetail(extra));
      if(snapshot!==this.activityShippingSnapshot&&this.recordWholesaleActivity('shipping_quote_viewed',extra))this.activityShippingSnapshot=snapshot;
    } catch { /* Unknown visibility is not a customer view. */ }
  }

  async wholesaleActivityHeaders() {
    if(this.config.activityEnabled!==true||!this.gatewayReady())return null;
    const cached=this.cachedCheckoutSession();
    if(this.purchasing&&!cached)return null;
    const session=cached||await this.checkoutSessionForRequest();
    return {'X-TTH-Session':session.sessionToken,'X-TTH-CSRF':session.csrfToken};
  }

  activityIdentityHeaders() {
    try {
      const identity=this.wholesaleActivity?.getIdentity();
      return /^[a-f0-9]{32}$/.test(identity?.cartId)&&/^[a-f0-9]{32}$/.test(identity?.clientSessionId)
        ?{'X-TTH-Cart-ID':identity.cartId,'X-TTH-Client-Session-ID':identity.clientSessionId}:{};
    } catch { return {}; }
  }

  flushWholesaleActivity() { try { void this.wholesaleActivity?.flush()?.catch(()=>{}); } catch {} }

  refreshAttemptIdentity() {
    const attemptKey = `${this.config.storageKey}:checkout-attempt`;
    if (!this.identities.length) {
      this.idempotencyKey = '';
      this.attemptFingerprint = '';
      this.addressRevision = '';
      storageRemove('sessionStorage', attemptKey);
      return;
    }
    const lines = this.identities.map(({ productId, variantId, quantity }) => ({ productId, variantId, quantity }))
      .sort((a, b) => a.variantId.localeCompare(b.variantId) || a.productId.localeCompare(b.productId));
    const basket = { lines, currency: this.config.currency, discountPercent: this.config.discountPercent, minimumSubtotalCents: this.config.minimumSubtotalCents, minimumPieces: this.config.minimumPieces };
    const previous = parseJSON(storageRead('sessionStorage', attemptKey));
    if (!this.addressRevisionRestored) {
      const previousIntent = parseJSON(previous?.fingerprint);
      if (previousIntent) {
        const { addressRevision, ...previousBasket } = previousIntent;
        if (JSON.stringify(previousBasket) === JSON.stringify(basket) && /^[a-f0-9]{64}$/.test(addressRevision || '')) this.addressRevision = addressRevision;
      }
      this.addressRevisionRestored = true;
    }
    const fingerprint = JSON.stringify({ ...basket, addressRevision: this.addressRevision });
    if (this.attemptFingerprint === fingerprint && this.idempotencyKey) return;
    this.idempotencyKey = previous?.fingerprint === fingerprint && /^[-a-zA-Z0-9_:.]{8,200}$/.test(previous.key) ? previous.key : randomKey();
    this.attemptFingerprint = fingerprint;
    storageWrite('sessionStorage', attemptKey, JSON.stringify({ fingerprint, key: this.idempotencyKey }));
  }

  registerProduct(raw, { inline = false } = {}) {
    const productId = id(raw?.id, 'Product');
    if (!productId || !text(raw?.title) || !Array.isArray(raw?.variants)) return null;
    const product = {
      unitLabel: raw.variants.length === 1 && /^set\b/i.test(text(raw.title)) ? 'set' : 'piece',
      id: productId, title: text(raw.title), handle: text(raw.handle, 200), type: text(raw.type, 150),
      createdAt: typeof raw.createdAt === 'number' && Number.isFinite(raw.createdAt) && raw.createdAt > 0 ? raw.createdAt : typeof raw.createdAt === 'string' && Number.isFinite(Date.parse(raw.createdAt)) ? Date.parse(raw.createdAt) : 0,
      tags: Array.isArray(raw.tags) ? raw.tags.map(tag => text(tag, 100)).slice(0, 200) : text(raw.tags, 2000),
      vendor: text(raw.vendor, 150), owned: raw.owned === true, family: text(raw.family, 50),
      category: text(raw.category, 100), glassware: raw.glassware === true, allowedFamily: raw.allowedFamily === true,
      optionNames: Array.isArray(raw.optionNames ?? raw.options) ? (raw.optionNames ?? raw.options).map(option => text(typeof option === 'string' ? option : option?.name, 100)).slice(0, 20) : [],
      image: publicURL(typeof raw.image === 'object' ? raw.image?.src : raw.image),
      url: publicURL(raw.url || (raw.handle ? `/products/${encodeURIComponent(raw.handle)}` : ''), true), variants: []
    };
    const seen = new Set();
    for (const rawVariant of raw.variants.slice(0, 2000)) {
      const variantId = id(rawVariant?.id);
      const previous = this.variants.get(variantId);
      if (!variantId || seen.has(variantId) || previous && previous.product.id !== productId) continue;
      seen.add(variantId);
      product.variants.push({
        id: variantId, title: text(rawVariant.title, 200), sku: text(rawVariant.sku, 100),
        costWholesaleCents: rawVariant.pricingSource === 'shopify-metafield' ? integer(rawVariant.costWholesaleCents) : null,
        pricingSource: rawVariant.pricingSource === 'shopify-metafield' ? 'shopify-metafield' : '',
        available: rawVariant.available === true, price: integer(rawVariant.price, true),
        compareAtPrice: integer(rawVariant.compareAtPrice, true), pieces: integer(rawVariant.pieces),
        options: Array.isArray(rawVariant.options) ? rawVariant.options.map(option => text(option, 200)).slice(0, 20) : null,
        requiresShipping: rawVariant.requiresShipping === true,
        inventoryTracked: rawVariant.inventoryTracked === true,
        inventoryQuantity: integer(rawVariant.inventoryQuantity, true),
        ...(Object.hasOwn(rawVariant, 'sellableOnlineQuantity') ? { sellableOnlineQuantity: integer(rawVariant.sellableOnlineQuantity, true) } : {}),
        image: publicURL(typeof rawVariant.image === 'object' ? rawVariant.image?.src : rawVariant.image)
      });
    }
    if (!product.variants.length) return null;
    const previous = this.products.get(productId);
    previous?.variants.forEach(variant => this.variants.delete(variant.id));
    this.products.set(productId, product);
    product.variants.forEach(variant => this.variants.set(variant.id, { product, variant }));
    if (inline) this.inlineProducts.set(productId, product);
    return product;
  }

  variantInfo(product, variant) {
    const policy = wholesaleEligibility(product, variant);
    const stock = wholesaleStock(variant);
    const offerCents = wholesaleUnitCents({ retailCents: variant.price, regularCents: variant.compareAtPrice }, this.config.discountPercent);
    const pieces = integer(variant.pieces);
    const unitLabel = product.unitLabel || 'piece';
    const owned = product.owned === true && product.vendor.toLowerCase() === 'the talavera hub';
    const eligible = policy.eligible === true && !!pieces && offerCents > 0;
    const spendEligible = eligible || policy.spendEligible === true;
    const pricingMode = spendEligible ? 'wholesale' : 'regular';
    const unitCents = eligible ? offerCents : policy.spendEligible && variant.costWholesaleCents > 0 ? Math.min(variant.price,variant.costWholesaleCents) : variant.price;
    const available = variant.available === true && stock.available === true;
    const reason = !owned ? 'This product is not available for direct wholesale ordering.' : !variant.requiresShipping ? 'This option needs a custom order.' : !(unitCents > 0) ? 'Contact us for pricing.' : !available ? stock.reason || 'Currently out of stock.' : '';
    return { eligible, spendEligible, available, unitCents, regularCents: Math.max(variant.price || 0, variant.compareAtPrice || 0), pieces, unitLabel, pricingMode, availableQuantity: stock.availableQuantity, reason, offerReason: policy.reason, buyable: !reason };
  }

  loadCostPrices(){ return Promise.resolve(); }

  purchaseGroups(product) { return buildPurchaseGroups(product, { discountPercent: this.config.discountPercent }).groups; }

  pieceGroup(productId, groupKey) {
    const product = this.products.get(id(productId, 'Product'));
    return product && this.purchaseGroups(product).find(group => group.mode === 'pieces' && group.key === groupKey);
  }

  groupForVariant(product, variantId) {
    return this.purchaseGroups(product).find(group => group.variantIds.includes(variantId));
  }

  groupQuantity(group) {
    const members = new Set(group.variantIds);
    const pieces = new Map(group.stockVariants.filter(stock => members.has(stock.variantId)).map(stock => [stock.variantId, stock.pieces]));
    return this.identities.reduce((sum, line) => sum + (pieces.has(line.variantId) ? line.quantity * (pieces.get(line.variantId) || 0) : 0), 0);
  }

  planForGroup(group, quantity) {
    const members = new Set(group.variantIds);
    const reservedQuantities = new Map(this.identities.filter(line => !members.has(line.variantId)).map(line => [line.variantId, line.quantity]));
    return planExactPieces(group, quantity, { reservedQuantities });
  }

  groupStockProblem() {
    for (const productId of new Set(this.identities.map(line => line.productId))) {
      const product = this.products.get(productId);
      if (product && this.purchaseGroups(product).some(group => {
        if (group.mode !== 'pieces') return false;
        const stock = new Map(group.stockVariants.map(row => [row.variantId, row.pieces]));
        const selected = this.identities.filter(row => stock.has(row.variantId));
        if (selected.length > 1 && selected.some(row => !stock.get(row.variantId))) return true;
        return selected.reduce((sum, row) => sum + row.quantity * (stock.get(row.variantId) || 0), 0) > group.physicalStockCap;
      })) return true;
    }
    return false;
  }

  groupAdditionProblem(product, variantId, count) {
    const quantities = new Map(this.identities.map(line => [line.variantId, line.quantity]));
    quantities.set(variantId, (quantities.get(variantId) || 0) + count);
    return this.purchaseGroups(product).some(group => {
      if (group.mode !== 'pieces' || !group.stockVariants.some(stock => stock.variantId === variantId)) return false;
      const selected = group.stockVariants.filter(stock => quantities.get(stock.variantId));
      if (selected.length > 1 && selected.some(stock => !stock.pieces)) return true;
      return selected.reduce((sum, stock) => sum + quantities.get(stock.variantId) * (stock.pieces || 0), 0) > group.physicalStockCap;
    });
  }

  setGroupQuantity(productId, groupKey, quantity) {
    if (this.purchasing || !Number.isSafeInteger(quantity) || quantity < 0 || quantity > MAX_QUANTITY) return false;
    const group = this.pieceGroup(productId, groupKey);
    if (!group) { this.status('Please choose this product option again.'); return false; }
    const memberIds = new Set(group.variantIds);
    const outside = this.identities.filter(line => !memberIds.has(line.variantId));
    const plan = quantity ? this.planForGroup(group, quantity) : { ok: true, lines: [] };
    if (!plan.ok) {
      this.status(plan.code === 'quantity_tiers_unverified' ? 'Pricing for these options needs confirmation. Please request a custom quote.' : plan.code === 'insufficient_stock' || plan.code === 'reserved_stock_exceeded' ? `Only ${group.physicalStockCap} pieces available for ${group.styleLabel}.` : 'That exact quantity is not available. Please choose another quantity.');
      return false;
    }
    const replacement = plan.lines.map(line => ({ productId: group.productId, variantId: line.variantId, quantity: line.quantity }));
    if (new Set([...outside, ...replacement].map(line => line.variantId)).size > this.config.maximumLines) { this.status(`Wholesale checkout supports up to ${this.config.maximumLines} product options.`); return false; }
    const first = this.identities.findIndex(line => memberIds.has(line.variantId));
    const insertion = first < 0 ? outside.length : this.identities.slice(0, first).filter(line => !memberIds.has(line.variantId)).length;
    outside.splice(insertion, 0, ...replacement);
    this.identities = outside;
    this.notice = 'Wholesale order updated.';
    this.persist(); this.render(); this.renderBuilders();
    return true;
  }

  adjustGroup(productId, groupKey, direction) {
    const group = this.pieceGroup(productId, groupKey);
    if (!group || !['increase', 'decrease'].includes(direction)) return false;
    return this.setGroupQuantity(productId, groupKey, this.groupQuantity(group) + (direction === 'increase' ? 1 : -1));
  }

  commitGroupQuantity(input) {
    const group = this.pieceGroup(input?.dataset.productId, input?.dataset.groupPieceQuantity);
    if (!group) return false;
    const previous = this.groupQuantity(group);
    let quantity = integer(input.value, true);
    const maximum = Math.min(MAX_QUANTITY, group.physicalStockCap);
    if (quantity > maximum) { quantity = maximum; input.value = String(maximum); this.toast(`Only ${maximum} in stock.`); }
    if (quantity === previous) return true;
    if (quantity === null || !this.setGroupQuantity(group.productId, group.key, quantity)) { input.value = String(previous); return false; }
    return true;
  }

  allocationDescription(lines) {
    return lines.map(line => line.pieces === 1 ? `${line.quantity} ${line.quantity === 1 ? 'single' : 'singles'}` : `${line.quantity > 1 ? `${line.quantity} × ` : ''}${line.pieces}-piece ${line.quantity > 1 ? 'packs' : 'pack'}`).join(' + ');
  }

  displayLines() {
    const grouped = new Map();
    const rows = [];
    for (const line of this.lines) {
      const product = this.products.get(line.productId);
      const group = product && this.groupForVariant(product, line.variantId);
      if (!group || group.mode !== 'pieces' || line.pending) { rows.push(line); continue; }
      let row = grouped.get(group.key);
      if (!row) {
        row = { ...line, group, groupKey: group.key, variantId: '', variantTitle: group.styleLabel === shortTitle(line.title) ? '' : group.styleLabel, sku: this.variants.get(group.baseVariantId)?.variant.sku || '', pieces: 1, quantity: 0, totalCents: 0, eligiblePieces: 0, eligibleSubtotalCents: 0, allocation: [], availableQuantity: group.physicalStockCap, reason: '' };
        grouped.set(group.key, row); rows.push(row);
      }
      row.quantity += line.quantity * line.pieces;
      row.totalCents += line.quantity * line.unitCents;
      row.eligiblePieces += line.eligible && line.available ? line.quantity * line.pieces : 0;
      row.eligibleSubtotalCents += line.spendEligible && line.available ? line.quantity * line.unitCents : 0;
      row.available &&= line.available; row.buyable &&= line.buyable;
      row.reason ||= line.reason;
      row.allocation.push(line);
    }
    for (const row of grouped.values()) {
      row.unitCents = row.totalCents / row.quantity;
      row.eligible = row.eligiblePieces === row.quantity;
      row.spendEligible = row.eligibleSubtotalCents > 0;
      row.pricingMode = row.spendEligible ? 'wholesale' : 'regular';
      if (row.quantity > row.availableQuantity) { row.available = false; row.reason = `Only ${row.availableQuantity} pieces available. Reduce this quantity.`; }
    }
    return rows;
  }

  hydrate() {
    this.lines = this.identities.map(identity => {
      const entry = this.variants.get(identity.variantId);
      if (!entry || entry.product.id !== identity.productId) {
        return { ...identity, title: 'Checking product availability…', sku: '', image: '', url: '', pieces: null, unitCents: null, eligible: false, available: false, reason: this.catalogError || 'Checking this saved selection against the current catalog.', pending: true };
      }
      const { product, variant } = entry;
      let info = this.variantInfo(product, variant);
      const group = this.groupForVariant(product, variant.id);
      if (group?.mode === 'pieces' && group.baseVariantId === variant.id) {
        // A base single is sold in exact pieces using the current quantity tier.
        // Saved native pack identities retain their own original unit semantics.
        const reservedQuantities = new Map(this.identities.filter(line => line.variantId !== variant.id).map(line => [line.variantId, line.quantity]));
        const tier = planExactPieces(group, identity.quantity, { reservedQuantities });
        if (tier.ok) info = { ...info, ...tier.lines[0], available: info.available, buyable: info.buyable, availableQuantity: tier.maximumAvailablePieces, tierPieces: tier.tierPieces, tierVariantId: tier.tierVariantId };
        else info = { ...info, available: false, buyable: false, reason: tier.code === 'insufficient_stock' ? `Only ${tier.maximumAvailablePieces} pieces available. Reduce this quantity.` : 'This quantity needs to be checked again.' };
      }
      const overStock = Number.isSafeInteger(info.availableQuantity) && identity.quantity > info.availableQuantity;
      return { ...identity, ...info, available: info.available && !overStock, reason: overStock ? `Only ${info.availableQuantity} available. Reduce this quantity.` : info.reason, title: product.title, variantTitle: variant.title, sku: variant.sku, image: variant.image || product.image, url: product.url, pending: false };
    });
    this.volumeSavings=wholesaleVolumeSavings(this.lines,this.config.volumeDiscountEnabled);
    this.lines=this.volumeSavings.lines;
  }

  hasUnresolvedLines() { return this.identities.some(line => this.variants.get(line.variantId)?.product.id !== line.productId); }

  orderCount() {
    return this.displayLines().reduce((total, line) => total + line.quantity, 0);
  }

  qualification() {
    const summary = qualifyOrder(this.lines, this.config);
    const distinctOptions = new Set(this.identities.map(line => line.variantId)).size;
    const overLineLimit = distinctOptions > this.config.maximumLines;
    const blocked = this.catalogRevalidationRequired || overLineLimit || this.groupStockProblem() || this.lines.some(line => line.pending || !line.buyable || !line.available || !(line.unitCents > 0) || line.eligible && !line.pieces || !Number.isSafeInteger(line.availableQuantity) || line.quantity > line.availableQuantity);
    return { ...summary, qualified: summary.qualified && !blocked && !this.pendingLegacy.length && this.currencySupported(), blocked, overLineLimit, distinctOptions };
  }

  finishLegacy() {
    if (!this.pendingLegacy.length) return;
    let skipped = 0;
    const merged = new Map(this.identities.map(row => [row.variantId, row]));
    for (const legacy of this.pendingLegacy) {
      const entry = this.resolvePayload(legacy, true);
      const quantity = integer(legacy._legacySource === 'quote' ? legacy.requestedQty : legacy.quantity);
      // Old quantities were individual pieces. Never turn an ambiguous pack count into a purchase.
      if (!entry || entry.variant.pieces !== 1 || !quantity || quantity > MAX_QUANTITY || !this.variantInfo(entry.product, entry.variant).buyable) { skipped++; continue; }
      const previous = merged.get(entry.variant.id);
      merged.set(entry.variant.id, { productId: entry.product.id, variantId: entry.variant.id, quantity: Math.max(quantity, previous?.quantity || 0) });
    }
    this.identities = [...merged.values()];
    this.pendingLegacy = [];
    this.notice = skipped ? `Your previous selections were checked. ${skipped} selection${skipped === 1 ? ' needs' : 's need'} to be chosen again because the option, pack size, or availability could not be confirmed.` : 'Your previous selections now use current wholesale pricing.';
    this.persist();
  }

  resolvePayload(payload, legacy = false) {
    const variantId = id(payload?.variantId || payload?.variant_id);
    const productId = id(payload?.productId || payload?.product_id || (legacy ? payload?.id : ''), 'Product');
    if (variantId) {
      const entry = this.variants.get(variantId);
      if (entry && (!productId || productId === entry.product.id)) return entry;
      return null;
    }
    if (!productId) return null;
    const product = this.products.get(productId);
    if (!product) return null;
    const sku = text(payload?.sku, 100).toLowerCase();
    const candidates = sku ? product.variants.filter(variant => variant.sku.toLowerCase() === sku) : product.variants.length === 1 ? product.variants : [];
    return candidates.length === 1 ? { product, variant: candidates[0] } : null;
  }

  async loadCardProduct(productId,opener){
    this.cardProductRequests||=new Map();
    if(this.cardProductRequests.has(productId))return this.cardProductRequests.get(productId);
    const task=(async()=>{
      this.cardProductSlots||=[];this.cardProductActive||=0;
      if(this.cardProductActive>=4)await new Promise(resolve=>this.cardProductSlots.push(resolve));
      this.cardProductActive++;
      try{return await this.fetchCardProduct(productId,opener);}finally{this.cardProductActive--;this.cardProductSlots.shift()?.();}
    })();
    this.cardProductRequests.set(productId,task);const result=await task;if(!result)this.cardProductRequests.delete(productId);return result;
  }
  async fetchCardProduct(productId,opener){
    const card=opener?.closest('[data-retail-card]');
    const link=card?.querySelector('a[href*="/products/"]');
    if(link){try{const url=new URL(link.href,location.origin);url.search='';url.searchParams.set('view','tth-wholesale-data');const response=await fetch(url.href,{credentials:'same-origin'});if(!response.ok)throw Error('Product unavailable');const payload=await response.json();const product=this.registerProduct(payload.product);if(product&&product.id===String(productId))return product;}catch{}}
    return null;
  }
  async readCatalogPage(url){
    const safe=publicURL(url,true);if(!safe)throw Error('Invalid catalog continuation');
    const abort=new AbortController(),timer=setTimeout(()=>abort.abort(),25000);
    try{const response=await fetch(safe,{credentials:'same-origin',signal:abort.signal});if(!response.ok)throw Error('Catalog unavailable');const payload=await response.json();if(!Array.isArray(payload.products)||payload.currency&&payload.currency!==this.config.currency)throw Error('Catalog data unavailable');const candidate=payload.next??payload.nextURL??payload.nextUrl;
      let next='';if(candidate){if(typeof candidate!=='string')throw Error('Invalid continuation');const continuation=new URL(candidate,safe);for(const name of ['view','sort_by']){const value=new URL(this.config.sourceURL).searchParams.get(name);if(value)continuation.searchParams.set(name,value);}next=publicURL(continuation.href,true);if(!next||next===safe)throw Error('Invalid continuation');}return {products:payload.products,next};
    }finally{clearTimeout(timer);}
  }
  prefetchCatalogPage(){
    if(!this.catalogNext||this.catalogPrefetch?.url===this.catalogNext)return;
    const url=this.catalogNext;this.catalogPrefetch={url,promise:this.readCatalogPage(url).then(payload=>({payload}),()=>({payload:null}))};
  }
  async changeCatalogSort(sort){
    if(this.catalogPromise)await this.catalogPromise;
    const url=new URL(this.config.sourceURL);if((url.searchParams.get('sort_by')||'best-selling')===sort)return;
    url.searchParams.set('sort_by',sort);url.searchParams.delete('page');this.config.sourceURL=url.href;
    this.cacheKey=`tth-wholesale-catalog-v10:${url.href}:${this.config.currency}`;this.catalogVisibleIds=new Set();this.catalogNext=undefined;this.catalogVisited=new Set();this.catalogPrefetch=null;this.catalogComplete=false;
    return this.loadCatalog();
  }
  matchingCatalogCount(){
    const builder=[...this.builders].find(node=>node.isConnected),state=builder&&this.builderStates.get(builder);
    return [...this.products.values()].filter(product=>!this.catalogVisibleIds||this.catalogVisibleIds.has(product.id)).filter(product=>product.owned&&this.visibleVariants(product).length&&(!state?.category||this.browseGroups(product).includes(state.category))&&(!state?.wholesaleOnly||this.buyableVariants(product).some(variant=>this.variantInfo(product,variant).spendEligible))&&matchesProduct(product,state?.query||'')).length;
  }
  async loadCatalog({force=false,more=false,all=false,selected=false}={}){
    if(this.catalogPromise){await this.catalogPromise;if(!more&&!all&&!selected)return;}
    if(!this.config.sourceURL)return;
    if(force){this.catalogComplete=false;this.catalogNext=undefined;this.catalogPrefetch=null;this.catalogVisited=new Set();}
    if(this.catalogComplete&&!force)return;
    if(this.catalogNext===undefined&&!force){
      const cached=parseJSON(storageRead('sessionStorage',this.cacheKey));
      if(cached?.currency===this.config.currency&&Date.now()-cached.savedAt<CACHE_AGE&&Date.now()>=cached.savedAt&&Array.isArray(cached.products)){
        cached.products.forEach(product=>{this.registerProduct(product);this.catalogVisibleIds?.add(product.id);});this.catalogNext=cached.next||'';this.catalogComplete=!this.catalogNext;void this.loadCostPrices([...this.products.values()]).then(()=>{this.render();this.renderBuilders();});this.render();this.renderBuilders();this.prefetchCatalogPage();if(!more&&!all&&!force&&!selected)return;
      }
    }
    if(this.catalogNext===undefined)this.catalogNext=this.config.sourceURL;
    if(!this.catalogNext)return;
    const builder=[...this.builders].find(node=>node.isConnected),state=builder&&this.builderStates.get(builder);
    const target=state?.limit||PAGE_SIZE;
    this.catalogLoading=true;this.catalogError='';this.renderBuilders();
    this.catalogPromise=(async()=>{
      try{
        let loaded=0;this.catalogVisited||=new Set();
        do{
          const url=this.catalogNext;if(this.catalogVisited.has(url)||this.catalogVisited.size>=200)throw Error('Catalog continuation repeated');
          const buffered=this.catalogPrefetch?.url===url?await this.catalogPrefetch.promise:null;
          const payload=buffered?.payload||await this.readCatalogPage(url);this.catalogVisited.add(url);this.catalogPrefetch=null;
          const page=payload.products.map(raw=>this.registerProduct(raw)).filter(Boolean);page.forEach(product=>this.catalogVisibleIds?.add(product.id));this.catalogNext=payload.next;this.catalogComplete=!payload.next;loaded++;
          this.render();this.renderBuilders();await this.loadCostPrices(page);this.render();this.renderBuilders();
          // A filtered view may need another server page to fill its 24 visible cards.
        }while(this.catalogNext&&(all||force||this.matchingCatalogCount()<target||(selected||!builder)&&this.hasUnresolvedLines()));
        if(this.catalogComplete)this.finishCatalog();
        if(selected&&!this.hasUnresolvedLines())this.catalogRevalidationRequired=false;
        this.cacheSelectedProducts();
        const cache=JSON.stringify({currency:this.config.currency,savedAt:Date.now(),products:[...this.products.values()].filter(product=>!this.catalogVisibleIds||this.catalogVisibleIds.has(product.id)),next:this.catalogNext});if(cache.length<6000000)storageWrite('sessionStorage',this.cacheKey,cache);
      }catch(error){console.warn('[TTH wholesale] Catalog page unavailable');this.catalogError='Products could not be loaded. Try Load more products again.';}
      finally{this.catalogLoading=false;this.catalogPromise=null;this.render();this.renderBuilders();if(!all&&!force)this.prefetchCatalogPage();}
    })();return this.catalogPromise;
  }

  finishCatalog() {
    this.catalogRevalidationRequired = false;
    this.finishLegacy();
    const before = this.identities.length;
    this.identities = this.identities.filter(line => this.variants.get(line.variantId)?.product.id === line.productId);
    if (this.identities.length !== before) {
      this.notice = 'Some saved selections are no longer in the public catalog and were removed. Please choose any replacement products.';
      this.persist();
    }
    this.render();
    this.renderBuilders();
  }

  render() {
    this.hydrate();
    this.refreshAttemptIdentity();
    const summary = this.qualification();
    const count = this.orderCount();
    const fab = this.querySelector('[data-order-fab]');
    if (fab) { fab.hidden = this.checkoutPage || !this.identities.length && !this.pendingLegacy.length; fab.setAttribute('aria-label', `Review wholesale order, ${count} ${count === 1 ? 'item' : 'items'}`); }
    this.querySelectorAll('[data-order-count]').forEach(node => { node.textContent = String(count); });
    const empty = this.querySelector('[data-order-empty]');
    if (empty) empty.hidden = this.identities.length > 0 || this.pendingLegacy.length > 0;
    const list = this.querySelector('[data-order-lines]');
    if (list) list.replaceChildren(...this.displayLines().map(line => this.renderLine(line)));
    const totals = this.querySelector('[data-order-summary]');
    if (totals) {
      totals.replaceChildren(...this.summaryNodes(summary));
    }
    const progress = this.querySelector('[data-order-progress]');
    if (progress) {
      if (!this.currencySupported()) progress.textContent = `Wholesale orders start at ${this.money(this.config.minimumSubtotalCents, this.config.baseCurrency)} ${this.config.baseCurrency} after discount and ${this.config.minimumPieces} pieces. Prices shown are ${this.config.currency}; please contact us to confirm the currency conversion before purchase.`;
      else if (this.pendingLegacy.length || this.lines.some(line => line.pending)) progress.textContent = 'Checking your saved selections and current availability…';
      else if (this.catalogRevalidationRequired) progress.textContent = this.catalogLoading ? 'Checking current prices and stock…' : this.catalogError || 'Please refresh to confirm current prices and stock.';
      else if (summary.overLineLimit) progress.textContent = `Wholesale checkout supports up to ${this.config.maximumLines} distinct product options. Remove ${summary.distinctOptions - this.config.maximumLines} ${summary.distinctOptions - this.config.maximumLines === 1 ? 'option' : 'options'} before continuing. Quantities of the same option count as one option.`;
      else if (summary.blocked) progress.textContent = 'Review unavailable items or quantities before continuing.';
      else if (summary.qualified) progress.textContent = 'Wholesale minimum met.';
      else {
        const remaining = [];
        if (summary.remainingCents) remaining.push(`${this.money(summary.remainingCents)} more`);
        if (summary.remainingPieces) remaining.push(`${summary.remainingPieces} more ${summary.remainingPieces === 1 ? 'piece' : 'pieces'}`);
        progress.textContent = `Add ${remaining.join(' and ')} in eligible items.`;
      }
    }
    if(progress)progress.hidden=summary.qualified;
    const purchase = this.querySelector('[data-order-purchase]');
    if (purchase) { purchase.hidden = !summary.qualified || this.checkoutPage; purchase.disabled = !!this.purchasing; }
    this.renderAddressStep();
    this.renderDeliveryShipping();
    if (!this.addressStep || !this.addressForm) this.renderCheckoutPage(summary, progress?.textContent || '');
    const meter = this.querySelector('[data-order-progress-bar]');
    if (meter) {
      meter.hidden=summary.qualified;
      meter.max = 100;
      meter.value = Math.min(100, Math.max(0, Math.min(summary.subtotalCents / this.config.minimumSubtotalCents, summary.pieces / this.config.minimumPieces) * 100));
      meter.setAttribute('aria-label', `Wholesale minimum progress: ${Math.round(meter.value)} percent`);
    }
    this.renderQuickMinimum();
    this.renderRecommendations();
    const status = this.querySelector('[data-order-status]');
    if (status) status.textContent = this.notice;
    this.refreshGuides(true);
    this.dispatchEvent(new CustomEvent('th:wholesale-order-change', { bubbles: true, composed: true, detail: { count, ...summary, currency: this.config.currency } }));
  }

  toast(message) {
    let toast = document.querySelector('[data-tth-wholesale-toast]');
    if (!toast) { toast = element('p', 'tth-cart-toast'); toast.dataset.tthWholesaleToast = ''; toast.setAttribute('role','status'); document.body.append(toast); }
    toast.textContent = message; toast.hidden = false; clearTimeout(this.toastTimer); this.toastTimer = setTimeout(()=>{toast.hidden=true;},4000);
  }

  summaryNodes(summary) {
    const nodes = [element('span', 'tth-wo__subtotal', `Subtotal: ${this.money(summary.orderSubtotalCents ?? summary.subtotalCents)}`)];
    if(this.config.volumeDiscountEnabled){
      if(this.volumeSavings?.discountCents>0)nodes.push(element('span','tth-wo__volume-savings',`Includes an extra 5% off: ${this.money(this.volumeSavings.discountCents)} saved.`));
      else if(this.volumeSavings?.remainingCents>0){const link=element('a','tth-wo__volume-offer',`Add ${this.money(this.volumeSavings.remainingCents)} in qualifying merchandise for an extra 5% off →`);const url=new URL(this.config.checkoutURL,location.origin);url.searchParams.delete('wholesale_checkout');link.href=url.href;nodes.push(link);}
    }
    if (summary.regularSubtotalCents > 0) nodes.push(element('span', 'tth-wo__meta', `${this.money(summary.subtotalCents)} eligible for the wholesale minimum`));
    return nodes;
  }

  renderCheckoutPage(summary, progress) {
    if (!this.checkoutPage || !this.checkoutMount) return;
    const empty=!this.identities.length&&!this.pendingLegacy.length;
    const emptyNode=this.checkoutMount.querySelector('[data-wholesale-checkout-empty]');
    if(emptyNode){emptyNode.hidden=!empty;if(empty&&!emptyNode.children.length){const link=element('a','tth-wo__purchase','Shop wholesale');const destination=new URL(this.config.checkoutURL,location.origin);destination.searchParams.delete('wholesale_checkout');link.href=destination.href;emptyNode.append(element('h2','','Your checkout is empty'),element('p','','Add products to your wholesale order, then return here to check out.'),link);}else if(!empty)emptyNode.replaceChildren();}
    if(this.checkoutSummary)this.checkoutSummary.hidden=empty;
    const intro=this.checkoutMount.querySelector('.tth-checkout-page__header > p:last-child');if(intro)intro.hidden=empty;
    if(empty){this.checkoutItems?.replaceChildren();this.checkoutTotals?.replaceChildren();return;}
    const summaryHeading=this.checkoutSummary?.querySelector('.tth-wo__checkout-summary-heading');if(summaryHeading&&summaryHeading.tagName!=='H2')summaryHeading.replaceWith(element('h2',summaryHeading.className,summaryHeading.textContent));
    this.checkoutItems?.replaceChildren(...this.displayLines().map(line => this.renderLine(line, { readOnly: true })));
    const state = text(formField(this.addressForm, 'provinceCode')?.value, 2).toUpperCase();
    const shipping = this.shippingPreview(state, summary.subtotalCents);
    const total = summary.orderSubtotalCents ?? summary.subtotalCents;
    const delivery=this.checkoutSummary?.querySelector('[data-order-delivery-slot]');

    const summaryNodes=this.summaryNodes(summary);
    const volume=summaryNodes.filter(node=>node.matches('.tth-wo__volume-offer,.tth-wo__volume-savings'));
    if(!this.checkoutVolume&&this.checkoutItems){this.checkoutVolume=element('div','tth-wo__checkout-volume');this.checkoutItems.before(this.checkoutVolume);}
    this.checkoutVolume?.replaceChildren(...volume);
    this.checkoutTotals?.replaceChildren(summaryNodes[0],
      ...(delivery?[delivery]:[]), ...summaryNodes.slice(1).filter(node=>!volume.includes(node)),

      ...(shipping.supported&&this.shippingPostal()?[element('strong','tth-wo__checkout-total',`Estimated total before tax: ${this.money(total+shipping.amountCents)}`)]:[]),
      ...(!summary.qualified ? [element('p', 'tth-wo__note', progress || 'Add eligible items to meet the wholesale minimum.')] : []),
      ...(!summary.qualified && this.addressMessage ? [element('p', 'tth-wo__warning', this.addressMessage)] : []));
    this.renderDeliveryShipping();
  }

  renderLine(line, { readOnly = false } = {}) {
    const row = element('li', 'tth-wo__line');
    row.dataset.variantId = line.variantId;
    if (line.image) row.append(image(line.image, 'tth-wo__image'));
    else { const placeholder = element('span', 'tth-wo__image'); placeholder.setAttribute('aria-hidden', 'true'); row.append(placeholder); }
    const details = element('div', 'tth-wo__details');
    const title = line.url ? element('a', 'tth-wo__title', shortTitle(line.title)) : element('strong', 'tth-wo__title', shortTitle(line.title));
    if (line.url) title.href = line.url;
    details.append(title);
    if (line.variantTitle && line.variantTitle !== 'Default Title') details.append(element('span', 'tth-wo__meta', line.variantTitle));
    if (line.sku) details.append(element('span', 'tth-wo__meta', `SKU: ${line.sku}`));
    if (line.unitCents > 0) {
      details.append(element('span', 'tth-wo__meta', line.groupKey ? `${line.totalCents % line.quantity ? '~' : ''}${this.money(line.totalCents / line.quantity)} / ${line.unitLabel || 'piece'}` : `${this.priceDescription(line.unitCents, line.pieces, line.unitLabel)}${line.pricingMode === 'regular' ? ' · regular pricing' : ''}`));
    }
    if (line.groupKey && line.eligiblePieces < line.quantity) details.append(element('span', 'tth-wo__meta', line.eligiblePieces ? `${line.eligiblePieces} eligible pieces` : line.eligibleSubtotalCents > 0 ? 'Counts toward spend only' : 'Regular pricing'));
    if (line.groupKey && line.allocation.some(item => item.pieces > 1)) {
      const allocation = element('details', 'tth-wo__allocation');
      allocation.append(element('summary', '', 'Saved selection'), element('p', '', this.allocationDescription(line.allocation)));
      details.append(allocation);
    }
    if (line.reason) details.append(element('span', 'tth-wo__meta tth-wo__warning', line.reason));
    row.append(details);
    if (readOnly) {
      row.append(element('span', 'tth-wo__quantity', `${line.pieces > 1 ? 'Sets' : 'Quantity'}: ${line.quantity}`));
      row.append(element('strong', 'tth-wo__total', line.buyable && line.available && line.unitCents > 0 ? this.money(line.totalCents ?? line.quantity * line.unitCents) : 'Needs review'));
      return row;
    }
    const qtyLabel = element('div', 'tth-wo__quantity');
    const input = quantityInput(line.quantity, `Quantity for ${shortTitle(line.title)}${line.variantTitle && line.variantTitle !== 'Default Title' ? ', ' + line.variantTitle : ''}`);
    if (line.groupKey) { input.dataset.groupPieceQuantity = line.groupKey; input.dataset.productId = line.productId; }
    else { input.dataset.lineQuantity = ''; input.dataset.variantId = line.variantId; }
    if (Number.isSafeInteger(line.availableQuantity)) input.max = String(Math.min(MAX_QUANTITY, line.availableQuantity));
    input.disabled = !!this.purchasing;
    const decrease = button('tth-wo__quantity-step', '−');
    if (line.groupKey) { decrease.dataset.groupAdjust = 'decrease'; decrease.dataset.groupKey = line.groupKey; decrease.dataset.productId = line.productId; }
    else { decrease.dataset.lineAdjust = 'decrease'; decrease.dataset.variantId = line.variantId; }
    decrease.setAttribute('aria-label', `Decrease quantity for ${shortTitle(line.title)}`);
    decrease.disabled = !!this.purchasing;
    const increase = button('tth-wo__quantity-step', '+');
    if (line.groupKey) { increase.dataset.groupAdjust = 'increase'; increase.dataset.groupKey = line.groupKey; increase.dataset.productId = line.productId; }
    else { increase.dataset.lineAdjust = 'increase'; increase.dataset.variantId = line.variantId; }
    increase.setAttribute('aria-label', `Increase quantity for ${shortTitle(line.title)}`);
    increase.disabled = !!this.purchasing || line.quantity >= Math.min(MAX_QUANTITY, line.availableQuantity || 0);
    const control = element('div', 'tth-wo__quantity-control');
    control.append(decrease, input, increase); qtyLabel.append(control);
    if (line.availableQuantity > 0) qtyLabel.append(element('span', 'tth-wo__meta', `${line.availableQuantity} available`));
    row.append(qtyLabel);
    row.append(element('strong', 'tth-wo__total', line.buyable && line.available && line.unitCents > 0 ? this.money(line.totalCents ?? line.quantity * line.unitCents) : 'Needs review'));
    const remove = button('tth-wo__remove', 'Remove', line.groupKey ? '' : 'remove-line');
    if (line.groupKey) { remove.dataset.removeGroup = line.groupKey; remove.dataset.productId = line.productId; }
    else remove.dataset.variantId = line.variantId;
    remove.disabled = !!this.purchasing;
    remove.setAttribute('aria-label', `Remove ${shortTitle(line.title)} from wholesale order`);
    row.append(remove);
    return row;
  }

  priceDescription(unitCents, pieces, unitLabel = 'piece') {
    if (!pieces) return `${this.money(unitCents)} / selected option`;
    if (pieces === 1) return `${this.money(unitCents)} / ${unitLabel}`;
    const approximation = unitCents % pieces ? '~' : '';
    return `${this.money(unitCents)} / set · ${approximation}${this.money(Math.round(unitCents / pieces))} / piece`;
  }

  open(opener) {
    void this.ensureShippingPolicy();
    if (this.checkoutPage) { this.checkoutMount?.scrollIntoView({ block: 'start', behavior: 'smooth' }); return; }
    let guideOpener;
    this.guides.forEach((_, root) => {
      const dialog = root.querySelector('[data-wholesale-guide-dialog]');
      if (dialog?.open) guideOpener ||= dialog._tthOpener;
      closeDialog(dialog);
    });
    this.render();
    showDialog(this.dialog, opener || guideOpener);
    if(this.pendingLegacy.length)void this.loadCatalog();else if(this.hasUnresolvedLines())void this.refreshSavedSelections();
    void this.ensureRecommendations();
  }

  add(product, variant, quantity, { open = true } = {}) {
    const count = integer(quantity);
    const info = this.variantInfo(product, variant);
    if (!count || count > MAX_QUANTITY || !info.buyable || this.purchasing) {
      this.status(info.reason || 'Choose an available option and a whole quantity from 1 to 9,999.');
      return false;
    }
    const previous = this.identities.find(row => row.variantId === variant.id);
    if (this.groupAdditionProblem(product, variant.id, count)) {
      this.status('This quantity exceeds the verified stock for this style, or another selected pack needs its piece count confirmed.');
      return false;
    }
    if (!previous && new Set(this.identities.map(row => row.variantId)).size >= this.config.maximumLines) {
      this.status(`Wholesale checkout supports up to ${this.config.maximumLines} distinct product options. Increase the quantity of an existing option, or remove one before adding another.`);
      return false;
    }
    if (previous && previous.quantity + count > MAX_QUANTITY) { this.status('The maximum quantity for one option is 9,999.'); return false; }
    if (count + (previous?.quantity || 0) > info.availableQuantity) {
      this.status(`Only ${info.availableQuantity} available for ${shortTitle(product.title)}${previous ? `; ${previous.quantity} already in your order` : ''}.`);
      return false;
    }
    if (previous) previous.quantity += count;
    else this.identities.push({ variantId: variant.id, productId: product.id, quantity: count });
    this.notice = `${shortTitle(product.title)} added to your wholesale order.`;
    this.persist();
    this.render();
    this.renderBuilders();
    if (open) this.open();
    return true;
  }

  addLegacyItem(payload) {
    const entry = this.resolvePayload(payload, true);
    if (!entry) {
      this.status('Please choose the product option again so its current price and availability can be checked.');
      if (!this.catalogComplete) void this.loadCatalog();
      return false;
    }
    const explicitQuantity = integer(payload?.quantity);
    const legacyQuantity = integer(payload?.requestedQty);
    const quantity = explicitQuantity || (entry.variant.pieces === 1 ? legacyQuantity : null);
    if (!quantity) { this.status('Please choose the option and quantity again before adding this set.'); return false; }
    return this.add(entry.product, entry.variant, quantity);
  }

  setQuantity(variantId, quantity) {
    if (this.purchasing) return;
    const selected = this.identities.find(line => line.variantId === id(variantId));
    if (!selected || !Number.isInteger(quantity) || quantity < 0 || quantity > MAX_QUANTITY) return;
    if (!quantity) this.identities = this.identities.filter(line => line !== selected);
    else {
      const entry = this.variants.get(selected.variantId);
      const info = entry && this.variantInfo(entry.product, entry.variant);
      if (!info?.buyable || quantity > info.availableQuantity) {
        this.status(info?.availableQuantity > 0 ? `Only ${info.availableQuantity} available. Reduce this quantity.` : info?.reason || 'This option is unavailable.');
        this.render();
        return false;
      }
      if (quantity > selected.quantity && this.groupAdditionProblem(entry.product, selected.variantId, quantity - selected.quantity)) { this.status('This quantity exceeds the verified stock for this style, or another selected pack needs its piece count confirmed.'); return false; }
      selected.quantity = quantity;
    }
    this.notice = '';
    this.persist();
    this.render();
    this.renderBuilders();
    return true;
  }

  commitLineQuantity(input) {
    const selected = this.identities.find(line => line.variantId === id(input?.dataset.variantId));
    if (!selected) return false;
    const quantity = integer(input.value);
    if (!quantity || quantity > MAX_QUANTITY) {
      this.status('Enter a whole quantity from 1 to 9,999.');
      input.value = String(selected.quantity);
      return false;
    }
    // Change normally runs before focusout; the second event is a harmless no-op.
    if (quantity === selected.quantity) return true;
    const committed = this.setQuantity(selected.variantId, quantity);
    if (!committed) input.value = String(selected.quantity);
    return committed;
  }

  adjustLine(variantId, direction) {
    const line = this.identities.find(item => item.variantId === id(variantId));
    if (!line || !['increase', 'decrease'].includes(direction)) return false;
    return this.setQuantity(line.variantId, line.quantity + (direction === 'increase' ? 1 : -1));
  }

  clear() {
    if (this.purchasing) return;
    this.identities = [];
    this.pendingLegacy = [];
    this.notice = '';
    this.persist();
    this.render();
    this.renderBuilders();
  }

  async purchase() {
    this.hydrate();
    if(!this.purchasing)this.recordWholesaleActivity('checkout_clicked');
    if(!this.purchasing&&!this.qualification().qualified)this.recordWholesaleActivity(this.qualification().blocked?'checkout_failed':'minimum_blocked',{reasonCode:this.qualification().blocked?'basket_changed':'minimum_not_met'});
    if (this.purchasing || !this.qualification().qualified) { this.render(); return; }
    this.refreshAttemptIdentity();
    if (this.checkoutPage) { this.renderAddressStep(); return; }
    if (!this.config.checkoutURL) { this.status(CHECKOUT_UNAVAILABLE); return; }
    window.location.assign(this.config.checkoutURL);
  }

  populateAddressStates() {
    const select = formField(this.addressForm, 'provinceCode');
    if (!select || select.options?.length > 1) return;
    US_STATES.forEach(([code, name]) => select.append(new Option(name, code)));
  }

  addressReviewSnapshot() {
    return JSON.stringify([...ADDRESS_FIELDS, 'email'].map(name => [name, String(formField(this.addressForm, name)?.value || '')]));
  }

  checkoutDeliveryRoot() {
    return this.checkoutSummary?.querySelector('[data-wholesale-delivery]')
      || this.querySelector('[data-order-delivery-slot] [data-wholesale-delivery]');
  }

  addressReviewText(key) { return this.config.addressReviewCopy?.[key] || ADDRESS_REVIEW_COPY[key]; }

  addressDestinationEdited(event) {
    if (this.purchasing) return;
    clearTimeout(this.addressDeliveryTimer);
    this.addressDestinationReview = null;
    this.deliveryDestinationDismissed = null;
    this.deliveryDestinationIntent = null;
    this.deliveryDestinationNeedsRequest = true;
    this.addressMessage = ['provinceCode','zip'].includes(event?.target?.name)&&text(formField(this.addressForm,'address1')?.value)&&text(formField(this.addressForm,'city')?.value)
      ?this.addressReviewText('reminder'):'';
    this.renderAddressStep();
    this.syncAddressDelivery();
    this.renderDeliveryShipping();
  }

  refreshAddressShipping() {
    this.shippingPolicy?.invalidate?.();
    this.shippingPolicyRefresh = null;
    void this.ensureShippingPolicy();
  }

  installShippingPreview(){
    const catalogEstimator=document.querySelector('.tth-wb__shipping [data-wholesale-delivery]');
    const clearCatalogQuote=()=>{this.deliveryPostal='';this.renderDeliveryShipping();};
    catalogEstimator?.addEventListener('input',clearCatalogQuote,{signal:this.controller.signal});
    catalogEstimator?.addEventListener('change',clearCatalogQuote,{signal:this.controller.signal});
    const estimator = this.checkoutDeliveryRoot();
    this.deliveryDestinationBaseline = this.addressReviewSnapshot();
    if (estimator) {
      const invalidate = () => {
        if (this.purchasing) return;
        clearTimeout(this.addressDeliveryTimer);
        this.addressDestinationReview = null;
        this.deliveryDestinationDismissed = null;
        this.deliveryDestinationIntent = null;
        this.deliveryDestinationNeedsRequest = true;
        this.addressMessage = '';
        this.renderAddressStep();
      };
      estimator.addEventListener('input', invalidate, { capture: true, signal:this.controller.signal });
      estimator.addEventListener('change', invalidate, { capture: true, signal:this.controller.signal });
      const remember = postal => {
        if (this.purchasing) return;
        if (!this.deliveryDestinationResetting) this.deliveryDestinationDismissed=null;
        const zip5 = destinationZip5(postal);
        this.deliveryDestinationIntent = zip5 ? { postal:zip5, snapshot:this.addressReviewSnapshot() } : null;
        this.deliveryDestinationNeedsRequest = true;
      };
      estimator.addEventListener('submit', () => remember(estimator.querySelector('input[name="postal"]')?.value), { capture:true, signal:this.controller.signal });
      estimator.addEventListener('tth:estimate-destination', event => remember(event.detail?.postal), { capture:true, signal:this.controller.signal });
    }
    document.addEventListener('tth:delivery-destination',event=>this.receiveDeliveryDestination(event),{signal:this.controller.signal});
    this.addEventListener('th:wholesale-order-change',()=>this.renderDeliveryShipping(),{signal:this.controller.signal});
    this.renderDeliveryShipping();
  }

  receiveDeliveryDestination(event) {
    if (this.purchasing || this.controller.signal.aborted) return;
    const {country,state,postal}=event.detail||{};
    const zip5=destinationZip5(postal);
    if(country!=='US'||!zip5||!US_STATES.some(([code])=>code===state))return;
    if (this.checkoutPage) {
      const estimator=this.checkoutDeliveryRoot();
      if (!estimator || event.target!==estimator || !estimator.isConnected) return;
      const selectedCountry=estimator.querySelector('select[name="country"]')?.value || 'US';
      if (selectedCountry!=='US'||destinationZip5(estimator.querySelector('input[name="postal"]')?.value)!==zip5) return;
      const snapshot=this.addressReviewSnapshot(),intent=this.deliveryDestinationIntent;
      if (intent ? intent.postal!==zip5||intent.snapshot!==snapshot : this.deliveryDestinationNeedsRequest||this.deliveryDestinationBaseline!==snapshot) return;
      const currentState=text(formField(this.addressForm,'provinceCode')?.value,2).toUpperCase();
      const currentPostal=destinationZip5(formField(this.addressForm,'zip')?.value);
      const dismissed=this.deliveryDestinationDismissed;
      if (dismissed?.state===state&&dismissed.postal===zip5&&dismissed.snapshot===snapshot) return;
      if (currentState===state&&currentPostal===zip5) {
        this.addressDestinationReview=null;
      } else {
        const filled=['address1','address2','city','provinceCode','zip'].some(name=>text(formField(this.addressForm,name)?.value));
        if (filled) {
          this.addressDestinationReview={state,postal:zip5,snapshot,intent};
          this.recordWholesaleActivity('address_review_required',{reasonCode:'destination_review_pending'});
          this.addressMessage='';
          this.renderAddressStep();
          return;
        }
        const stateField=formField(this.addressForm,'provinceCode'),zipField=formField(this.addressForm,'zip');
        if(stateField)stateField.value=state;
        if(zipField)zipField.value=zip5;
        this.deliveryDestinationIntent={postal:zip5,snapshot:this.addressReviewSnapshot()};
        this.refreshAddressShipping();
      }
    }
    this.deliveryState=state;
    this.deliveryPostal=zip5;
    this.renderAddressStep();this.renderDeliveryShipping();
  }

  resolveAddressDestination(accept) {
    const review=this.addressDestinationReview;
    if (this.purchasing||!review) return;
    const estimator=this.checkoutDeliveryRoot();
    const current=this.addressReviewSnapshot();
    if (review.snapshot!==current||review.intent!==this.deliveryDestinationIntent
      ||!estimator?.isConnected||destinationZip5(estimator.querySelector('input[name="postal"]')?.value)!==review.postal) {
      this.addressDestinationEdited();
      return;
    }
    clearTimeout(this.addressDeliveryTimer);
    this.addressDestinationReview=null;
    if (accept) {
      this.deliveryDestinationDismissed=null;
      const state=formField(this.addressForm,'provinceCode'),zip=formField(this.addressForm,'zip');
      if(state)state.value=review.state;
      if(zip&&destinationZip5(zip.value)!==review.postal)zip.value=review.postal;
      this.deliveryState=review.state;
      this.deliveryPostal=review.postal;
      this.deliveryDestinationIntent={postal:review.postal,snapshot:this.addressReviewSnapshot()};
      this.addressMessage=this.addressReviewText('reminder');
      this.refreshAddressShipping();
    } else {
      this.deliveryDestinationDismissed={state:review.state,postal:review.postal,snapshot:current};
      this.deliveryState=text(formField(this.addressForm,'provinceCode')?.value,2).toUpperCase();
      this.deliveryPostal=destinationZip5(formField(this.addressForm,'zip')?.value);
      this.deliveryDestinationIntent=null;
      this.deliveryDestinationNeedsRequest=true;
      this.addressMessage='';
      const input=estimator.querySelector('input[name="postal"]');
      if(input)input.value=this.deliveryPostal;
      // Reuse the normal estimator form action to cancel its old render generation.
      // Its async response can never change the address without a fresh review.
      {
        this.deliveryDestinationResetting=true;
        try { estimator.querySelector('[data-detb-form]')?.dispatchEvent(new Event('submit',{bubbles:true,cancelable:true})); }
        finally { this.deliveryDestinationResetting=false; }
      }
    }
    this.renderAddressStep();this.renderDeliveryShipping();
  }

  renderAddressDestinationReview() {
    const review=this.addressDestinationReview;
    let panel=this.addressDestinationReviewPanel;
    if (!review) { panel?.remove();this.addressDestinationReviewPanel=null;return; }
    if (!panel) {
      const parent=this.addressForm.querySelector('fieldset')||this.addressForm;
      panel=element('div','tth-wo__address-preview');
      panel.dataset.orderDestinationReview='';
      panel.setAttribute('role','group');
      panel.setAttribute('aria-label',this.addressReviewText('pending'));
      const message=element('p','tth-wo__note',this.addressReviewText('message'));
      const destination=element('p','tth-wo__note');destination.dataset.destinationReviewValue='';
      const reminder=element('p','tth-wo__note',this.addressReviewText('reminder'));
      const actions=element('div','tth-wo__address-actions');
      const accept=button('tth-wo__remove',this.addressReviewText('accept'));
      const cancel=button('tth-wo__remove',this.addressReviewText('cancel'));
      accept.dataset.destinationReviewAccept='';cancel.dataset.destinationReviewCancel='';
      accept.addEventListener('click',()=>this.resolveAddressDestination(true),{signal:this.controller.signal});
      cancel.addEventListener('click',()=>this.resolveAddressDestination(false),{signal:this.controller.signal});
      actions.append(accept,cancel);panel.append(message,destination,reminder,actions);
      parent.insertBefore(panel,parent.querySelector('.tth-wo__address-actions'));
      this.addressDestinationReviewPanel=panel;
    }
    panel.querySelector('[data-destination-review-value]').textContent=this.addressReviewText('destinationLabel')+' '+review.state+' '+review.postal;
    for(const node of panel.querySelectorAll('button'))node.disabled=!!this.purchasing;
  }

  shippingPostal(){
    const value=this.checkoutPage?formField(this.addressForm,'zip')?.value:this.deliveryPostal;
    return /^\d{5}(?:-\d{4})?$/.test(String(value||'').trim())?String(value).trim():'';
  }

  syncAddressDelivery(){
    clearTimeout(this.addressDeliveryTimer);
    const postal=this.shippingPostal();if(!postal)return;
    void this.ensureShippingPolicy();
    this.addressDeliveryTimer=setTimeout(()=>{
      const root=this.checkoutSummary?.querySelector('[data-wholesale-delivery]');
      root?.dispatchEvent(new CustomEvent('tth:estimate-destination',{detail:{country:'US',postal}}));
    },300);
  }

  paintShippingProgress(node,quote,summary){
    this.recordVisibleShipping(node,null,summary);
    node.replaceChildren();node.hidden=!this.shippingPostal();if(node.hidden)return;
    if(!quote.supported){node.append(element('p','',quote.verificationPending?'Confirming shipping. Continue to refresh your estimate.':'Contact us for shipping to this destination.'));return;}
    node.append(element('p','tth-wo__shipping-rate',quote.amountCents?'Shipping '+this.money(quote.amountCents):'Shipping: Free'));
    this.recordVisibleShipping(node,quote,summary);
    if(!summary.qualified&&!node.hasAttribute('data-shipping-progress-preview'))return;
    const meter=element('progress','tth-wo__free-progress');meter.max=quote.freeThresholdCents;meter.value=Math.min(summary.subtotalCents,quote.freeThresholdCents);meter.setAttribute('aria-label','Progress toward free wholesale shipping');
    node.append(meter,element('p','tth-wo__free-message',quote.remainingCents?'Spend '+this.money(quote.remainingCents)+' more for free shipping.':'Your order qualifies for free shipping.'));
  }

  renderDeliveryShipping(){
    const state=text(formField(this.addressForm,'provinceCode')?.value,2)||this.deliveryState;
    const summary=this.qualification(),quote=this.shippingPreview(state,summary.subtotalCents);
    for(const node of document.querySelectorAll('[data-order-zip-shipping]'))this.paintShippingProgress(node,quote,summary);
  }

  gatewayReady() {
    if (!this.config.checkoutEnabled || !this.config.checkoutBaseEndpoint || !window.crypto?.subtle) return false;
    const base = new URL(this.config.checkoutBaseEndpoint);
    return base.origin === window.location.origin && !base.search && !base.hash && base.pathname !== '/';
  }

  gatewayURL(action) {
    if (!this.gatewayReady() || !['session', 'checkout', 'shipping-policy'].includes(action)) return '';
    return `${this.config.checkoutBaseEndpoint.replace(/\/$/, '')}/${action}`;
  }

  shippingPolicyClient() {
    if (this.shippingPolicy) return this.shippingPolicy;
    if (!this.gatewayReady() || !this.config.requiredPolicyVersion) return null;
    try {
      this.shippingPolicy = createShippingPolicyClient({
        requiredCheckoutPolicyVersion: this.config.requiredPolicyVersion,
        requireApprovedPolicy: true,
        origin: window.location.origin,
        endpoint: this.gatewayURL('shipping-policy')
      });
    } catch { return null; }
    return this.shippingPolicy;
  }

  async ensureShippingPolicy() {
    const client = this.shippingPolicyClient();
    if (!client || this.shippingPolicyRefresh) return this.shippingPolicyRefresh;
    const signal=this.controller.signal;
    const active=()=>!signal.aborted&&this.isConnected!==false&&!this.purchasing;
    if(!active())return;
    const wait=milliseconds=>new Promise(resolve=>{
      let timer;
      const done=()=>{window.clearTimeout(timer);signal.removeEventListener('abort',done);resolve();};
      signal.addEventListener('abort',done,{once:true});
      if(signal.aborted){done();return;}
      timer=window.setTimeout(done,milliseconds);
    });
    // Retry read-only preview metadata after transient cold-start failures only.
    // The foreground purchase fence and all checkout writes keep their own flow.
    const pending = Promise.resolve().then(async()=>{
      for(let attempt=0;attempt<3;attempt++){
        if(!active()||this.shippingPolicyRefresh!==pending)return;
        const snapshot=await client.refresh({force:attempt>0,signal});
        if(!active()||this.shippingPolicyRefresh!==pending||snapshot?.ready
          ||!['request_timeout','request_unavailable'].includes(snapshot?.reason)||attempt===2)return;
        await wait((attempt+1)*1000);
      }
    });
    this.shippingPolicyRefresh = pending;
    try { await pending; }
    catch { /* Cancellation leaves checkout on hold. */ }
    finally {
      const current=this.shippingPolicyRefresh===pending;
      if (current) this.shippingPolicyRefresh = null;
      if (current&&active()) { this.renderAddressStep(); this.renderDeliveryShipping(); }
    }
  }

  shippingAssessment(provinceCode, subtotalCents = this.qualification().subtotalCents, address) {
    if (!this.currencySupported() || this.config.baseCurrency !== 'USD') return { quote: { supported: false }, purchaseAllowed: false, compatibility: null };
    return this.shippingPolicyClient()?.getQuote({
      address: address || { countryCode: 'US', provinceCode: text(provinceCode, 2).toUpperCase(), zip: this.shippingPostal() },
      subtotalCents, currency: this.config.currency
    }) || { quote: { supported: false }, purchaseAllowed: false, compatibility: null };
  }

  shippingPreview(provinceCode, subtotalCents = this.qualification().subtotalCents) {
    const assessment = this.shippingAssessment(provinceCode, subtotalCents);
    return assessment.quote.supported && !assessment.purchaseAllowed
      ? { supported: false, verificationPending: true }
      : assessment.quote;
  }

  renderAddressStep() {
    if (!this.addressStep || !this.addressForm) return;
    const qualified = this.qualification().qualified;
    this.addressStepOpen = !!(this.checkoutPage && this.checkoutMount && qualified);
    this.addressStep.hidden = !this.addressStepOpen;
    const purchase = this.querySelector('[data-order-purchase]');
    if (purchase) purchase.hidden = !qualified || this.checkoutPage;
    const state = text(formField(this.addressForm, 'provinceCode')?.value, 2).toUpperCase();
    const preview = this.shippingPreview(state);
    const node = this.addressNode('[data-order-shipping-preview]');
    if (node) {
      let message;
      if (!state) message = 'Choose your state to preview wholesale shipping.';
      else if (preview.verificationPending) message = 'Shipping needs confirmation. Continue to refresh your estimate.';
      else if (!preview.supported) message = 'Direct wholesale shipping is currently available in the contiguous United States and Washington, DC. Please contact us for other destinations.';
      else if (!preview.amountCents) message = 'Free wholesale shipping.';
      else message = `${this.money(preview.remainingCents, 'USD')} to free wholesale shipping.`;
      node.hidden=!this.shippingPostal()||this.checkoutPage&&preview.supported;
      if (node.textContent !== message) node.textContent = message;
    }
    const fieldset = this.addressForm.querySelector('fieldset');
    if (fieldset) fieldset.disabled = !!this.purchasing;
    const submit = this.addressNode('[data-order-address-submit]');
    if (submit) submit.disabled = !!this.purchasing || !!this.addressDestinationReview || !qualified || !this.shippingAssessment(state).quote.supported || !this.gatewayReady();
    this.renderAddressDestinationReview();
    const status = this.addressNode('[data-order-address-status]');
    const message = (this.addressDestinationReview ? this.addressReviewText('pending') : this.addressMessage) || (this.addressStepOpen && !this.gatewayReady() ? CHECKOUT_UNAVAILABLE : this.addressStepOpen && this.shippingPolicyClient()?.status().blocked ? 'Shipping needs confirmation. Continue to securely refresh the estimate before checkout.' : '');
    if (status && status.textContent !== message) status.textContent = message;
    this.renderCheckoutPage(this.qualification(), this.querySelector('[data-order-progress]')?.textContent || '');
  }

  readAddress() {
    const address = {};
    for (const name of ADDRESS_FIELDS) {
      const raw = formField(this.addressForm, name)?.value;
      const value = typeof raw === 'string' ? raw.normalize('NFKC').trim() : '';
      if (value.length > (name === 'address1' || name === 'address2' ? 255 : 120) || /[\r\n\x00-\x1f]/.test(value)) throw new Error('Please check the shipping address.');
      if (value) address[name] = value;
    }
    if (REQUIRED_ADDRESS_FIELDS.some(name => !address[name])) throw new Error('Please provide a complete shipping address.');
    address.provinceCode = address.provinceCode.toUpperCase();
    address.countryCode = address.countryCode.toUpperCase();
    if (address.countryCode !== 'US' || !CONTIGUOUS_STATES.has(address.provinceCode)) throw new Error('Direct wholesale shipping is currently available in the contiguous United States and Washington, DC.');
    if (!/^\d{5}(?:-\d{4})?$/.test(address.zip)) throw new Error('Please enter a valid 5-digit ZIP code or ZIP+4.');
    return address;
  }

  async addressDigest(address) {
    if (!window.crypto?.subtle) throw new Error('Please use a secure browser connection to continue.');
    const canonical = [...ADDRESS_FIELDS.map(name => [name, address[name] || '']), ['email',text(formField(this.addressForm,'email')?.value,254).toLowerCase()]];
    const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(JSON.stringify(canonical)));
    return [...new Uint8Array(digest)].map(byte => byte.toString(16).padStart(2, '0')).join('');
  }

  async gatewayJSON(url, options = {}) {
    const abort = new AbortController();
    const timeout = window.setTimeout(() => abort.abort(), 30000);
    try {
      const response = await fetch(url, { ...options, credentials: 'same-origin', cache: 'no-store', redirect: 'error', signal: abort.signal });
      const payload = await response.text();
      let result;
      try { result = JSON.parse(payload); }
      catch { const error=new Error('checkout_response_not_json');error.checkoutMessage='The checkout connection was interrupted. Please try again; your existing order attempt will be reused.';throw error; }
      return { response, result };
    } finally { window.clearTimeout(timeout); }
  }

  cachedCheckoutSession() {
    const saved=this.checkoutSession||parseJSON(storageRead('sessionStorage',`${this.config.storageKey}:checkout-session`));
    if(saved&&(!this.config.requiredPolicyVersion||saved.policyVersion===this.config.requiredPolicyVersion)&&opaqueToken(saved.sessionToken)&&opaqueToken(saved.csrfToken)&&expiryTime(saved.expiresAt)>Date.now()+30000){this.checkoutSession=saved;return saved;}
    return null;
  }

  async checkoutSessionForRequest() {
    const cached=this.cachedCheckoutSession();
    if(cached)return cached;
    if(this.checkoutSessionRequest)return this.checkoutSessionRequest;
    const request=this.prepareCheckoutSession();
    this.checkoutSessionRequest=request;
    try{return await request;}
    finally{if(this.checkoutSessionRequest===request)this.checkoutSessionRequest=null;}
  }

  async prepareCheckoutSession() {
    const key = `${this.config.storageKey}:checkout-session`;
    const saved = this.checkoutSession || parseJSON(storageRead('sessionStorage', key));
    if (saved && (!this.config.requiredPolicyVersion||saved.policyVersion===this.config.requiredPolicyVersion) && opaqueToken(saved.sessionToken) && opaqueToken(saved.csrfToken) && expiryTime(saved.expiresAt) > Date.now() + 30000) {
      this.checkoutSession = saved;
      return saved;
    }
    const headers = { Accept: 'application/json' };
    if (opaqueToken(saved?.sessionToken)) headers['X-TTH-Session'] = saved.sessionToken;
    const { response, result } = await this.gatewayJSON(this.gatewayURL('session'), { method: 'GET', headers });
    if (!response.ok || result?.ok !== true || !opaqueToken(result.sessionToken) || !opaqueToken(result.csrfToken) || expiryTime(result.expiresAt) <= Date.now() + 5000 || !text(result.policyVersion, 120)) {
      const error = new Error('session_resume_failed');
      error.checkoutMessage = text(result?.message, 300) || 'A secure wholesale session could not be prepared. Please try again.';
      throw error;
    }
    if(this.config.requiredPolicyVersion&&result.policyVersion!==this.config.requiredPolicyVersion){const error=new Error('pricing_policy_pending');error.checkoutMessage='Updated wholesale checkout is being prepared. Your selections are saved; please contact us to complete this order.';throw error;}
    const session = { sessionToken: result.sessionToken, csrfToken: result.csrfToken, expiresAt: expiryTime(result.expiresAt), policyVersion: text(result.policyVersion, 120) };
    this.checkoutSession = session;
    storageWrite('sessionStorage', key, JSON.stringify(session));
    return session;
  }

  async refreshRejectedCatalog() {
    this.catalogRevalidationRequired = true;
    this.catalogComplete = false;
    storageRemove('sessionStorage', this.cacheKey);
    this.render();
    // Do not let an older, overlapping load satisfy this explicit fresh check.
    if (this.catalogPromise) await this.catalogPromise;
    this.catalogRevalidationRequired = true;
    this.catalogComplete = false;
    storageRemove('sessionStorage', this.cacheKey);
    await this.loadCatalog({ force: true });
  }

  async sendCheckoutRequest(session, lines, shippingAddress, idempotencyKey, shippingCompatibility = null) {
    const send = () => this.gatewayJSON(this.gatewayURL('checkout'), {
      method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-TTH-Session': session.sessionToken, 'X-TTH-CSRF': session.csrfToken, ...this.activityIdentityHeaders() },
      body: JSON.stringify({ lines, shippingAddress, idempotencyKey, ...(shippingCompatibility || {}), ...(text(formField(this.addressForm,'email')?.value,254)?{email:text(formField(this.addressForm,'email')?.value,254).toLowerCase()}:{}) })
    });
    let result = await send();
    if (result.response.status === 409 && result.result?.code === 'session_recovery_only') {
      this.checkoutSession = null;
      storageRemove('sessionStorage', `${this.config.storageKey}:checkout-session`);
      session = await this.checkoutSessionForRequest();
      result = await send();
    }
    return {...result, session};
  }

  async submitAddress() {
    if (this.purchasing || !this.checkoutPage || !this.checkoutMount || !this.addressStepOpen || !this.addressForm) return;
    this.recordWholesaleActivity('checkout_clicked');
    if(!this.qualification().qualified){this.recordWholesaleActivity(this.qualification().blocked?'checkout_failed':'minimum_blocked',{reasonCode:this.qualification().blocked?'basket_changed':'minimum_not_met'});return;}
    if (this.addressDestinationReview) { this.recordWholesaleActivity('address_review_required',{reasonCode:'destination_review_pending'});this.addressMessage=this.addressReviewText('pending');this.renderAddressStep();return; }
    if (!this.gatewayReady()) { this.recordWholesaleActivity('checkout_failed',{reasonCode:'shipping_unconfirmed'});this.addressMessage = CHECKOUT_UNAVAILABLE; this.renderAddressStep(); return; }
    if (typeof this.addressForm.reportValidity === 'function' && !this.addressForm.reportValidity()) {this.recordWholesaleActivity('checkout_failed',{reasonCode:'address_incomplete'});return;}
    let address;
    try { address = this.readAddress(); }
    catch (error) { this.recordWholesaleActivity('checkout_failed',{reasonCode:'address_incomplete'});this.addressMessage = error.message; this.renderAddressStep(); return; }
    const previousShipping = this.shippingAssessment(address.provinceCode, this.qualification().subtotalCents, address);
    const preview = previousShipping.quote;
    if (!preview.supported) {this.recordWholesaleActivity('checkout_failed',{reasonCode:'shipping_unconfirmed'});return;}
    const selectedLines = this.identities.map(({ variantId, quantity }) => ({ variantId, quantity }));
    const basketSnapshot = JSON.stringify(selectedLines);
    this.purchasing = true;
    this.addressMessage = `Confirming your address and ${preview.amountCents ? `${this.money(preview.amountCents, 'USD')} estimated wholesale shipping` : 'free wholesale shipping'}…`;
    this.render();
    let checkoutStage = 'shipping';
    try {
      const client = this.shippingPolicyClient();
      if (!client) { this.recordWholesaleActivity('checkout_failed',{reasonCode:'shipping_unconfirmed'});this.addressMessage = 'Shipping could not be confirmed. Please try again.'; return; }
      const shippingReview = await client.refreshForPurchase({
        input: { address, subtotalCents: this.qualification().subtotalCents, currency: this.config.currency },
        previous: previousShipping, signal: this.controller.signal
      });
      if (shippingReview.status !== 'ready') {
        this.recordWholesaleActivity('checkout_failed',{reasonCode:'shipping_unconfirmed'});
        this.addressMessage = shippingReview.status === 'review_required'
          ? 'Your shipping estimate has been updated. Please review the total, then continue again.'
          : 'Shipping could not be confirmed. Your order is saved; please try again.';
        return;
      }
      // Compatibility metadata belongs to this verified quote. It does not
      // change the durable basket/address attempt identity on a retry.
      const shippingCompatibility = shippingReview.compatibility;
      checkoutStage = 'address';
      this.addressRevision = await this.addressDigest(address);
      this.refreshAttemptIdentity();
      const attemptKey = this.idempotencyKey;
      checkoutStage = 'session';
      let session = await this.checkoutSessionForRequest();
      if (!this.qualification().qualified || basketSnapshot !== JSON.stringify(this.identities.map(({ variantId, quantity }) => ({ variantId, quantity })))) {
        this.recordWholesaleActivity('checkout_failed',{reasonCode:'basket_changed'});
        this.addressMessage = 'Your wholesale order changed. Please review the selections and shipping estimate before continuing.';
        return;
      }
      if (await this.addressDigest(this.readAddress()) !== this.addressRevision) {
        this.recordWholesaleActivity('checkout_failed',{reasonCode:'address_incomplete'});
        this.addressMessage = 'Your address or email changed. Please review your shipping information, then continue again.';
        return;
      }
      checkoutStage = 'checkout';
      this.recordWholesaleActivity('checkout_requested',{shippingCents:shippingReview.quote.amountCents,freeThresholdCents:shippingReview.quote.freeThresholdCents});
      const checkout = await this.sendCheckoutRequest(session, selectedLines, address, attemptKey, shippingCompatibility);
      checkoutStage = 'verify';
      const {response, result} = checkout;
      session = checkout.session;
      if (!response.ok || result?.ok !== true) {
        this.recordWholesaleActivity(result?.code==='checkout_reconciliation_required'?'checkout_uncertain':'checkout_failed',{
          reasonCode:CATALOG_PREWRITE_ERRORS.has(result?.code)?'basket_changed':['invalid_address','address_unverified'].includes(result?.code)?'address_incomplete':['shipping_policy_changed','checkout_policy_review_required'].includes(result?.code)?'shipping_unconfirmed':'network_error',httpStatus:response.status});
        if ([401, 403].includes(response.status) && ['invalid_session', 'session_expired', 'invalid_csrf'].includes(result?.code)) {
          // Never replace the bearer after a CSRF/expiry failure: its durable
          // identity owns any completed or uncertain attempt. An explicit retry
          // resumes that SAME binding through GET and rotates only its CSRF.
          const stale = { ...session, expiresAt: 0 };
          this.checkoutSession = stale;
          storageWrite('sessionStorage', `${this.config.storageKey}:checkout-session`, JSON.stringify(stale));
        }
        this.addressMessage = text(result?.message, 300) || 'Your wholesale order could not be confirmed. Please review it and try again.';
        if (['shipping_policy_changed', 'checkout_policy_review_required'].includes(result?.code)) {
          await client.refresh({ force: true, signal: this.controller.signal });
          if (result?.code === 'shipping_policy_changed') this.addressMessage = 'Shipping has changed. Please review the refreshed estimate, then continue again. Your existing order attempt will be reused.';
        }
        if (CATALOG_PREWRITE_ERRORS.has(result?.code)) await this.refreshRejectedCatalog();
        return;
      }
      const invoice = publicURL(result.invoiceUrl);
      const url = invoice ? new URL(invoice) : null;
      const trustedHost = url && url.protocol === 'https:' && (url.origin === window.location.origin || url.hostname === 'checkout.shopify.com' || url.hostname.endsWith('.myshopify.com'));
      const subtotal = integer(result.summary?.subtotalCents);
      const regularSubtotal = integer(result.summary?.regularSubtotalCents ?? 0, true);
      const orderSubtotal = integer(result.summary?.orderSubtotalCents ?? (this.lines.some(line => line.pricingMode === 'regular') ? null : subtotal));
      const pieces = integer(result.summary?.pieces);
      const shipping = integer(result.shipping?.amountCents, true);
      if (!trustedHost || !subtotal || subtotal < this.config.minimumSubtotalCents || regularSubtotal === null || !orderSubtotal || orderSubtotal !== subtotal + regularSubtotal || !pieces || pieces < this.config.minimumPieces || shipping === null || result.shipping?.currency !== 'USD') throw new Error('unverified_checkout');
      const confirmedShipping = client.getQuote({ address, subtotalCents: subtotal, currency: 'USD' });
      if (!confirmedShipping.purchaseAllowed || !shippingCompatibility ||
          result.shipping.shippingPolicyVersion !== shippingCompatibility.shippingPolicyVersion ||
          result.shipping.shippingPolicyHash !== shippingCompatibility.shippingPolicyHash ||
          shipping !== shippingReview.quote.amountCents || shipping !== confirmedShipping.quote.amountCents ||
          result.shipping.freeThresholdCents !== confirmedShipping.quote.freeThresholdCents) {
        this.addressMessage = 'Your checkout needs a fresh price and shipping review. Your existing order attempt is saved; review the updated estimate, then try again.';
        this.recordWholesaleActivity('checkout_uncertain',{reasonCode:'shipping_unconfirmed',httpStatus:response.status});
        await this.refreshRejectedCatalog();
        await client.refresh({ force: true, signal: this.controller.signal });
        return;
      }
      this.addressMessage = `Your secure checkout is ready. Merchandise: ${this.money(orderSubtotal, 'USD')}. Shipping: ${this.money(shipping, 'USD')}. Review applicable taxes and your order before payment.`;
      this.renderAddressStep();
      this.recordWholesaleActivity('checkout_handoff',{shippingCents:shipping,freeThresholdCents:confirmedShipping.quote.freeThresholdCents});
      this.flushWholesaleActivity();
      window.location.assign(url.href);
    } catch (error) {
      this.recordWholesaleActivity(['checkout','verify'].includes(checkoutStage)?'checkout_uncertain':'checkout_failed',{reasonCode:'network_error'});
      console.warn('[TTH wholesale checkout]', checkoutStage, error?.name, error?.message);
      // A timeout is not proof that no draft was created. Keep the same persisted
      // session and attempt key, and let the server reconcile any repeated submit.
      this.addressMessage = typeof error?.checkoutMessage === 'string' ? error.checkoutMessage : 'We could not confirm your secure checkout yet. Please try again; your existing order attempt will be reused.';
    } finally {
      this.purchasing = false;
      this.render();
    }
  }

  attachBuilder(builder) {
    if (!builder?.isConnected || this.builders.has(builder) || this.checkoutPage) return;
    this.builders.add(builder);
    void this.ensureShippingPolicy();
    if (this.builderStates.has(builder)) { this.renderBuilder(builder); void this.loadCatalog(); return; }
    this.builderStates.set(builder, { query: '', category: '', wholesaleOnly: true, sort: 'best-selling', tab: 'all', limit: PAGE_SIZE, autoLoads: 0, choices: new Map() });
    const signal = this.controller.signal;
    builder.addEventListener('input', event => {
      const state = this.builderStates.get(builder);
      if (event.target.matches('[data-search]')) {
        state.query = event.target.value;
        state.limit = PAGE_SIZE; state.autoLoads = 0;
        this.renderBuilder(builder);
        clearTimeout(state.searchTimer);state.searchTimer=setTimeout(()=>{if(this.matchingCatalogCount()<state.limit&&!this.catalogComplete)void this.loadCatalog({more:true});},250);
      }
    }, { signal });
    builder.addEventListener('change', event => {
      const state = this.builderStates.get(builder);
      if (event.target.matches('[data-category]')) {
        state.category = event.target.value;
        state.limit = PAGE_SIZE; state.autoLoads = 0;
        this.renderBuilder(builder);
      }
      if (event.target.matches('[data-wholesale-only]')) { state.wholesaleOnly=event.target.checked;if(state.wholesaleOnly&&state.category==='fountains')state.category='';state.limit=PAGE_SIZE;state.autoLoads=0;this.renderBuilder(builder); }
      if (event.target.matches('[data-sort]')) {
        state.sort = event.target.value;
        state.limit = PAGE_SIZE; state.autoLoads = 0;
        this.renderBuilder(builder);
        void this.changeCatalogSort(state.sort);
      }
      if (event.target.matches('[data-group-piece-quantity]')) this.commitGroupQuantity(event.target);
      else if(this.matchingCatalogCount()<state.limit&&!this.catalogComplete)void this.loadCatalog({more:true});
    }, { signal });
    builder.addEventListener('focusout', event => {
      if (event.target.matches('[data-group-piece-quantity]')) this.commitGroupQuantity(event.target);
    }, { signal });
    builder.addEventListener('click', event => {
      const state = this.builderStates.get(builder);
      const adjustment = event.target.closest('[data-group-adjust]');
      if (adjustment) this.adjustGroup(adjustment.dataset.productId, adjustment.dataset.groupKey, adjustment.dataset.groupAdjust);
      if (event.target.closest('[data-load-more]')) { state.autoLoads=0;state.limit+=PAGE_SIZE;this.renderBuilder(builder);void this.loadCatalog({more:true}); }
      const tab = event.target.closest('[data-catalog-tab]');
      if (tab) {
        state.tab = ['all', 'best-selling', 'newest'].includes(tab.dataset.catalogTab) ? tab.dataset.catalogTab : 'all';
        state.sort = state.tab === 'newest' ? 'created-descending' : 'best-selling';
        state.limit = PAGE_SIZE; state.autoLoads = 0;
        this.renderBuilder(builder);
        void this.changeCatalogSort(state.sort);
      }
      const add = event.target.closest('[data-quick-add-product]');
      if (add) {
        if (!this.quickAdd(add.dataset.quickAddProduct, add)) {
          const status = builder.querySelector('[data-status]');
          if (status) status.textContent = this.notice;
        }
      }
    }, { signal });
    const more = builder.querySelector('[data-load-more]');
    const state = this.builderStates.get(builder);
    this.renderBuilder(builder);
    void this.loadCatalog();
  }

  renderBuilders() {
    this.builders.forEach(builder => {
      if (!builder.isConnected) this.builders.delete(builder);
      else this.renderBuilder(builder);
    });
  }

  browseGroups(product) {
    const value = [product.title,product.type,product.family,...(product.collections || []),...(Array.isArray(product.tags)?product.tags:[])].join(' ').toLowerCase();
    return [['alebrije',/alebrije/],['relief',/relief/],['guerrero',/guerrero/],['planters',/planter|flower pot/],['statues',/statue|statuette|figurine|sculpture/],['vases',/vase/],['fountains',/fountain|bird.?bath/],['drinkware',/mug|glass|drinkware|cup|jarrito/],['dinnerware',/dinnerware|plate|bowl|dish/],['decor',/decor|ornament|wall|candle/]].filter(([,pattern])=>pattern.test(value)).map(([key])=>key);
  }

  renderBuilder(builder) {
    const state = this.builderStates.get(builder);
    if (!state) return;
    // Map insertion order is the server's best-selling collection order.
    const products = [...this.products.values()].filter(product=>!this.catalogVisibleIds||this.catalogVisibleIds.has(product.id)).filter(product => product.owned && product.vendor.toLowerCase() === 'the talavera hub' && this.visibleVariants(product).length);
    const select = builder.querySelector('[data-category]');
    if (select) {
      const categories = ['alebrije','relief','guerrero','planters','statues','vases','drinkware','dinnerware','decor','fountains'].filter(key=>key!=='fountains'||!state.wholesaleOnly).filter(key=>key==='fountains'||products.some(product=>this.browseGroups(product).includes(key)));
      const labels={alebrije:'Alebrijes',relief:'Relief pottery',guerrero:'Guerrero',planters:'Planters',statues:'Statues & figurines',vases:'Vases',drinkware:'Drinkware & glassware',dinnerware:'Dinnerware',decor:'Decor',fountains:'Fountains & bird baths'};
      const signature = categories.join('\u0000');
      if (select.dataset.optionsSignature !== signature) {
        select.replaceChildren(new Option('All categories', ''), ...categories.map(category => new Option(labels[category], category)));
        select.dataset.optionsSignature = signature;
        select.value = state.category;
      }
    }
    const matching = this.sortProducts(products.filter(product => (!state.category || this.browseGroups(product).includes(state.category)) && (!state.wholesaleOnly || this.buyableVariants(product).some(variant=>this.variantInfo(product,variant).spendEligible)) && matchesProduct(product, state.query)), state.sort, state);
    const sort = builder.querySelector('[data-sort]');
    if (sort) sort.value = state.sort || 'best-selling';
    builder.querySelectorAll('[data-catalog-tab]').forEach(tab => tab.setAttribute('aria-pressed', String(tab.dataset.catalogTab === (state.tab || 'all'))));
    const shown = matching.slice(0, state.limit);
    const grid = builder.querySelector('[data-products]');
    if (grid) grid.replaceChildren(...shown.map(product => this.renderCard(product, state)));
    const count = builder.querySelector('[data-count]');
    if(count)count.textContent=shown.length?`Showing ${shown.length} products`:this.catalogLoading?'Loading products…':'';
    const empty = builder.querySelector('[data-empty]');
    if (empty) empty.hidden = matching.length > 0 || !this.catalogComplete;
    const more = builder.querySelector('[data-load-more]');
    if (more) {
      more.hidden=matching.length<=state.limit&&!this.catalogNext&&!this.catalogLoading;more.disabled=!!this.catalogLoading;more.textContent=this.catalogLoading?'Loading products…':'Load more products';
      if (state.observer && !more.hidden) { state.observer.unobserve(more); state.observer.observe(more); }
    }
    const status = builder.querySelector('[data-status]');
    if (status) status.textContent = this.catalogError || (!this.currencySupported() ? `Prices are shown in ${this.config.currency}. The wholesale minimum is ${this.money(this.config.minimumSubtotalCents, this.config.baseCurrency)} ${this.config.baseCurrency}; please contact us to confirm conversion before purchase.` : '');
  }

  renderCard(product, state) {
    const row = element('article', 'tth-wb__card');
    row.dataset.productId = product.id;
    const variant = this.cardVariant(product, state) || product.variants[0];
    const info = this.variantInfo(product, variant);
    const groups = this.purchaseGroups(product);
    const simple = groups.length === 1 && groups[0].mode === 'pieces' ? groups[0] : null;
    const currentPieces = simple ? this.groupQuantity(simple) : 0;
    const piecePlan = simple ? this.planForGroup(simple, currentPieces || 1) : null;
    const photoWrap = element('div', 'tth-wb__photo-wrap');
    const photo = element('a', 'tth-wb__photo');
    photo.href = product.url || '#';
    photo.setAttribute('aria-label', `View ${shortTitle(product.title)}`);
    if (variant.image || product.image) photo.append(image(variant.image || product.image, '', ''));
    photoWrap.append(photo);
    const variants = this.buyableVariants(product);
    if (simple) photoWrap.append(this.renderPieceControl(simple, currentPieces));
    else {
      const add = button('tth-wb__quick-add', '+');
      add.dataset.quickAddProduct = product.id;
      add.setAttribute('aria-label', `${variants.length > 1 ? 'Choose options for' : 'Add'} ${shortTitle(product.title)}${variants.length > 1 ? '' : ' to wholesale order'}`);
      add.disabled = !!this.purchasing || !variants.length;
      photoWrap.append(add);
    }
    row.append(photoWrap);
    const content = element('div', 'tth-wb__content');
    const title = element('a', 'tth-wb__title', shortTitle(product.title));
    title.href = product.url || '#';
    content.append(title);
    const price = element('p', 'tth-wb__price');
    price.append(element('strong', 'tth-wb__offer-price', simple && !piecePlan?.ok ? 'Quantity needs review' : piecePlan?.ok ? `${piecePlan.totalCents % piecePlan.requestedPieces ? '~' : ''}${this.money(piecePlan.totalCents / piecePlan.requestedPieces)} / ${product.unitLabel || 'piece'}` : `${this.money(info.unitCents)}${info.pieces > 1 ? ' / set' : info.pieces === 1 ? ' / ' + (product.unitLabel || 'piece') : ' / option'}`));
    if (info.spendEligible && info.regularCents > info.unitCents) price.append(element('span', 'tth-wb__regular-price', `MSRP ${this.money(info.regularCents)}`));
    content.append(price);
    const floor = this.quantityPriceFloor(product);
    const displayedUnit = piecePlan?.ok ? piecePlan.totalCents / (currentPieces || 1) : info.unitCents / (info.pieces || 1);
    if (floor && floor.offer.pieces > 1 && floor.plan.unitCents < displayedUnit) content.append(element('span', 'tth-wb__tier-hint', `As low as ${this.money(floor.plan.unitCents)} / piece at ${floor.offer.pieces}+`));
    if (piecePlan?.ok ? piecePlan.eligibleSubtotalCents === 0 : !info.spendEligible) content.append(element('span', 'tth-wb__note', 'Regular pricing'));
    if (info.availableQuantity > 0) content.append(element('span', 'tth-wb__stock', `${simple ? simple.physicalStockCap : info.availableQuantity}${!simple && info.pieces > 1 ? ' sets' : ''} available`));
    else content.append(element('span','tth-wb__stock','Out of stock'));
    row.append(content);
    return row;
  }

  quantityPriceFloor(product) {
    const candidates = this.purchaseGroups(product).filter(group => group.mode === 'pieces' && group.tierDefinitionsVerified).flatMap(group => group.offers.filter(offer => offer.pieces <= group.physicalStockCap).map(offer => ({group,offer,plan:this.planForGroup(group,offer.pieces)}))).filter(row => row.plan.ok);
    candidates.sort((a,b) => a.plan.unitCents-b.plan.unitCents || a.offer.pieces-b.offer.pieces);
    return candidates[0] || null;
  }

  renderPieceControl(group, quantity) {
    if (!quantity) {
      const add = button('tth-wb__quick-add', '+');
      add.dataset.groupAdjust = 'increase'; add.dataset.productId = group.productId; add.dataset.groupKey = group.key;
      add.setAttribute('aria-label', `Add one ${group.styleLabel} to wholesale order`);
      add.disabled = !!this.purchasing || !this.planForGroup(group, 1).ok;
      return add;
    }
    const control = element('div', 'tth-wb__piece-control');
    const decrease = button('tth-wb__piece-step', '−');
    const increase = button('tth-wb__piece-step', '+');
    for (const [node, direction] of [[decrease, 'decrease'], [increase, 'increase']]) {
      node.dataset.groupAdjust = direction; node.dataset.productId = group.productId; node.dataset.groupKey = group.key;
      node.setAttribute('aria-label', `${direction === 'increase' ? 'Add' : 'Remove'} one ${group.styleLabel}`);
    }
    decrease.disabled = !!this.purchasing || quantity <= 0;
    increase.disabled = !!this.purchasing || quantity >= Math.min(MAX_QUANTITY, group.physicalStockCap);
    const input = quantityInput(quantity, `Pieces in wholesale order for ${group.styleLabel}`);
    input.min = '0'; input.max = String(Math.min(MAX_QUANTITY, group.physicalStockCap)); input.disabled = !!this.purchasing;
    input.dataset.groupPieceQuantity = group.key; input.dataset.productId = group.productId;
    control.append(decrease, input, increase);
    return control;
  }

  visibleVariants(product) {
    return product.variants.filter(variant => variant.available === true && wholesaleStock(variant).available === true);
  }

  buyableVariants(product) {
    return this.visibleVariants(product).filter(variant => {
      const info = this.variantInfo(product, variant);
      return info.buyable && (this.identities.find(line => line.variantId === variant.id)?.quantity || 0) < Math.min(MAX_QUANTITY, info.availableQuantity);
    });
  }

  cardVariant(product, state = {}) {
    const firstPieceGroup = this.purchaseGroups(product).find(group => group.mode === 'pieces');
    if (firstPieceGroup) {
      const base = product.variants.find(variant => variant.id === firstPieceGroup.baseVariantId);
      if (base) return base;
    }
    const buyable = this.buyableVariants(product);
    const variants = buyable.length ? buyable : this.visibleVariants(product);
    return variants.find(variant => variant.id === state.choices?.get(product.id)) || variants.find(variant => this.variantInfo(product, variant).spendEligible && this.variantInfo(product, variant).buyable) || variants.find(variant => this.variantInfo(product, variant).buyable) || variants[0];
  }

  sortProducts(products, sort = 'best-selling', state = {}) {
    const rows = [...products];
    // Plan once per product, rather than once per comparator call. A selected
    // Simple cards show the charged per-piece tier at the current quantity.
    const prices = new Map();
    const price = product => {
      if (prices.has(product.id)) return prices.get(product.id);
      const groups = this.purchaseGroups(product);
      const simple = groups.length === 1 && groups[0].mode === 'pieces' ? groups[0] : null;
      const plan = simple ? this.planForGroup(simple, this.groupQuantity(simple) || 1) : null;
      const amount = simple ? plan?.ok ? plan.totalCents / plan.requestedPieces : null : this.variantInfo(product, this.cardVariant(product, state)).unitCents;
      const verified = Number.isFinite(amount) && amount > 0 ? amount : null;
      prices.set(product.id, verified);
      return verified;
    };
    const comparePrices = (a, b, direction) => {
      const left = price(a); const right = price(b);
      if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
      return direction * (left - right);
    };
    if (sort === 'title-ascending') rows.sort((a, b) => a.title.localeCompare(b.title));
    else if (sort === 'title-descending') rows.sort((a, b) => b.title.localeCompare(a.title));
    else if (sort === 'price-ascending') rows.sort((a, b) => comparePrices(a, b, 1));
    else if (sort === 'price-descending') rows.sort((a, b) => comparePrices(a, b, -1));
    else if (sort === 'created-descending') rows.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    return rows;
  }

  async quickAdd(productId, opener, { force = false, selectedVariantId = '', defaultPieces = null } = {}) {
    const product = this.products.get(id(productId, 'Product'));
    if (!product) { this.status('This product needs to be checked again. Please refresh the catalog.'); return false; }
    if(force&&this.quickAddDialog){
      this.quickAddProduct=product.id;
      this.querySelector('[data-wholesale-quick-add-title]').textContent=shortTitle(product.title);
      this.querySelector('[data-wholesale-quick-add-body]').replaceChildren(element('p','tth-wo__meta','Checking current wholesale pricing…'));
      showDialog(this.quickAddDialog,opener);
    }
    await this.loadCostPrices([product]);
    const groups = this.purchaseGroups(product);
    if (groups.length && groups.every(group => group.mode === 'pieces')) {
      const selected = groups.find(group => group.variantIds.includes(selectedVariantId)) || groups[0];
      if (groups.length === 1 && !force) return this.setGroupQuantity(product.id, selected.key, this.groupQuantity(selected) + 1);
      if (!this.quickAddDialog) { this.status('Please open the product to choose an option.'); return false; }
      this.quickAddProduct = product.id; this.quickAddMode = 'pieces';
      this.renderPieceChooser(selected.key, defaultPieces);
      showDialog(this.quickAddDialog, opener);
      return true;
    }
    const variants = this.buyableVariants(product);
    if (!variants.length) { this.status('The available quantity is already in your order, or this product is unavailable.'); return false; }
    if (variants.length === 1 && !force) return this.add(product, variants[0], 1, { open: false });
    if (!this.quickAddDialog) { this.status('Please open the product to choose an option.'); return false; }
    this.quickAddProduct = product.id;
    this.quickAddMode = 'variants';
    this.renderQuickAdd(variants.find(variant => variant.id === selectedVariantId)?.id || variants.find(variant => this.variantInfo(product, variant).eligible)?.id || variants[0].id);
    showDialog(this.quickAddDialog, opener);
    return true;
  }

  renderPieceChooser(groupKey, defaultPieces = null) {
    const product = this.products.get(this.quickAddProduct);
    const groups = product && this.purchaseGroups(product).filter(group => group.mode === 'pieces');
    const body = this.querySelector('[data-wholesale-quick-add-body]');
    if (!product || !groups?.length || !body) return;
    const group = groups.find(item => item.key === groupKey) || groups[0];
    this.quickAddGroupKey = group.key;
    const title = this.querySelector('[data-wholesale-quick-add-title]');
    if (title) title.textContent = shortTitle(product.title);
    const nodes = [];
    const base = product.variants.find(variant => variant.id === group.baseVariantId);
    const top = element('div', 'tth-wq__top');
    if (base?.image || product.image) top.append(image(base?.image || product.image, 'tth-wq__image'));
    const controls = element('div', 'tth-wq__controls'); top.append(controls); nodes.push(top);
    const details = element('div', 'tth-wq__details');
    if (groups.length > 1) {
      const label = element('label', 'tth-wq__variant', 'Choose a style');
      const select = element('select'); select.dataset.quickAddGroup = '';
      groups.forEach(item => select.append(new Option(item.styleLabel, item.key, false, item.key === group.key)));
      label.append(select); controls.append(label);
    } else if (group.styleValues.length) controls.append(element('p', 'tth-wq__style', group.styleLabel));
    controls.append(element('p', 'tth-wq__stock', `${group.physicalStockCap} ${product.unitLabel === 'set' ? 'sets' : 'pieces'} available`));
    const label = element('label', 'tth-wq__quantity', product.unitLabel === 'set' ? 'Sets' : 'Pieces');
    const quantity = defaultPieces ?? (this.groupQuantity(group) || 1);
    const input = quantityInput(quantity, `Total ${product.unitLabel === 'set' ? 'sets' : 'pieces'} for ${group.styleLabel}`);
    input.max = String(Math.min(MAX_QUANTITY, group.physicalStockCap)); input.dataset.quickAddPieceQuantity = '';
    label.append(this.quickQuantityControl(input)); controls.append(label);
    const preview = element('div', 'tth-wq__price'); preview.dataset.quickAddPiecePreview = ''; details.append(preview);
    const tier = element('p', 'tth-wo__meta'); tier.dataset.quickAddTier = ''; details.append(tier);
    const add = button('tth-wq__add tth-wo__purchase', this.groupQuantity(group) ? 'Update wholesale order' : 'Add to wholesale order');
    add.dataset.quickAddSubmit = ''; details.append(add);
    nodes.push(details); body.replaceChildren(...nodes);
    this.renderPiecePreview(integer(quantity));
    this.renderQuickMinimum();
  }

  renderPiecePreview(quantity) {
    const group = this.pieceGroup(this.quickAddProduct, this.quickAddGroupKey);
    const preview = this.querySelector('[data-quick-add-piece-preview]');
    if (!group || !preview) return;
    const plan = this.planForGroup(group, quantity);
    const add = this.querySelector('[data-quick-add-submit]');
    if (add) add.disabled = !!this.purchasing || !plan.ok;
    const status = this.querySelector('[data-wholesale-quick-add-status]');
    if (status) status.textContent = plan.ok ? '' : plan.code === 'quantity_tiers_unverified' ? 'Pricing for these options needs confirmation. Please request a custom quote.' : `Choose an exact quantity from 1 to ${Math.min(MAX_QUANTITY, group.physicalStockCap)} available pieces.`;
    if (!plan.ok) { preview.replaceChildren(); this.renderQuickMinimum(); return; }
    const unitLabel = this.products.get(group.productId)?.unitLabel || 'piece';
    const nodes = [element('strong', 'tth-wq__average', `${this.money(plan.totalCents / quantity)} / ${unitLabel}`)];
    const base = this.variants.get(group.baseVariantId);
    const regular = base && this.variantInfo(base.product, base.variant).regularCents;
    if (regular > plan.totalCents / quantity) nodes.push(element('span', 'tth-wq__retail', `MSRP ${this.money(regular)}`));
    if (!plan.eligiblePieces) nodes.push(element('span', 'tth-wo__meta', plan.eligibleSubtotalCents > 0 ? 'Counts toward $150 in merchandise; does not count toward the five qualifying pieces.' : 'Regular pricing; does not count toward wholesale minimums.'));
    preview.replaceChildren(...nodes);
    const tier = this.querySelector('[data-quick-add-tier]');
    if (tier) {
      const singlePrice = this.planForGroup(group, 1);
      const lowerTier = plan.tierPieces > 1 && singlePrice.ok && plan.unitCents < singlePrice.unitCents;
      tier.hidden = !lowerTier;
      tier.textContent = lowerTier ? `${plan.tierPieces}+ piece price applied to all ${quantity} pieces.` : '';
    }
    this.renderQuickMinimum();
  }

  quickQuantityControl(input) {
    const control = element('div', 'tth-wq__stepper');
    const minus = button('', '−'); minus.dataset.quickQuantityStep = '-1'; minus.setAttribute('aria-label', 'Decrease quantity');
    const plus = button('', '+'); plus.dataset.quickQuantityStep = '1'; plus.setAttribute('aria-label', 'Increase quantity');
    control.append(minus, input, plus); return control;
  }

  quickMinimumPreview() {
    if (this.quickAddMode === 'pieces') {
      const group = this.pieceGroup(this.quickAddProduct, this.quickAddGroupKey);
      const quantity = integer(this.querySelector('[data-quick-add-piece-quantity]')?.value);
      const plan = group && this.planForGroup(group, quantity);
      if (!plan?.ok) return null;
      const members = new Set(group.variantIds);
      const product = this.products.get(group.productId);
      return qualifyOrder([...this.lines.filter(line => !members.has(line.variantId)), ...plan.lines.map(line => ({ ...line, productId: group.productId, title: product.title, available: true }))], this.config);
    }
    const product = this.products.get(this.quickAddProduct);
    const variant = product?.variants.find(item => item.id === this.quickAddVariant);
    const quantity = integer(this.querySelector('[data-quick-add-quantity]')?.value);
    if (!variant || !quantity) return null;
    const info = this.variantInfo(product, variant);
    const already = this.identities.find(line => line.variantId === variant.id)?.quantity || 0;
    if (quantity + already > info.availableQuantity) return null;
    return qualifyOrder([...this.lines, { ...info, productId: product.id, variantId: variant.id, title: product.title, available: info.available, quantity }], this.config);
  }

  renderQuickMinimum() {
    const body = this.querySelector('[data-wholesale-quick-add-body]');
    if (!body) return;
    let panel = body.querySelector('[data-quick-minimum]');
    if (!panel) { panel = element('div', 'tth-wq__minimum'); panel.dataset.quickMinimum = ''; body.append(panel); }
    const projected = this.quickMinimumPreview();
    const summary = projected || this.qualification();
    const progress = element('progress', 'tth-wo__progress-bar'); progress.max = 100;
    progress.value = Math.min(100, Math.max(0, Math.min(summary.subtotalCents / this.config.minimumSubtotalCents, summary.pieces / this.config.minimumPieces) * 100));
    progress.setAttribute('aria-label', 'Wholesale minimum progress with this quantity');
    const remaining = [];
    if (summary.remainingCents) remaining.push(this.money(summary.remainingCents) + ' more');
    if (summary.remainingPieces) remaining.push(summary.remainingPieces + ' more ' + (summary.remainingPieces === 1 ? 'piece' : 'pieces'));
    const note = element('p', '', projected ? summary.qualified ? 'Wholesale minimum met.' : 'Add ' + remaining.join(' and ') + ' in eligible items.' : 'Choose an available quantity to preview your order.');
    const amount = element('p', 'tth-wq__minimum-amount'); amount.append(element('strong', '', 'Order subtotal: ' + this.money(summary.orderSubtotalCents)));
    const dispatch = this.querySelector('[data-order-dispatch]');
    const nodes = [amount];
    if (summary.regularSubtotalCents > 0) nodes.push(element('p', '', this.money(summary.subtotalCents) + ' eligible for the wholesale minimum'));
    nodes.push(note, progress);
    if (dispatch?.textContent) nodes.push(element('p', 'tth-wo__dispatch', dispatch.textContent));
    panel.replaceChildren(...nodes);
    for (const control of body.querySelectorAll('.tth-wq__stepper')) {
      const input = control.querySelector('input'), value = integer(input.value) || 0;
      control.querySelector('[data-quick-quantity-step="-1"]').disabled = value <= 1;
      control.querySelector('[data-quick-quantity-step="1"]').disabled = value >= Number(input.max);
    }
  }

  renderQuickAdd(variantId) {
    const product = this.products.get(this.quickAddProduct);
    const variants = product && this.buyableVariants(product);
    const body = this.querySelector('[data-wholesale-quick-add-body]');
    if (!product || !variants?.length || !body) { closeDialog(this.quickAddDialog); return; }
    const variant = variants.find(item => item.id === variantId) || variants[0];
    this.quickAddVariant = variant.id;
    const info = this.variantInfo(product, variant);
    const title = this.querySelector('[data-wholesale-quick-add-title]');
    if (title) title.textContent = shortTitle(product.title);
    const details = element('div', 'tth-wq__details');
    const nodes = [];
    if (variant.image || product.image) nodes.push(image(variant.image || product.image, 'tth-wq__image'));
    const label = element('label', 'tth-wq__variant', 'Choose an option');
    const select = element('select'); select.dataset.quickAddVariant = '';
    variants.forEach(item => {
      const current = this.variantInfo(product, item);
      select.append(new Option(`${item.title}${current.pricingMode === 'regular' ? ' · regular pricing' : ''}`, item.id, false, item.id === variant.id));
    });
    label.append(select); details.append(label);
    const price = element('p', 'tth-wq__price');
    price.append(element('strong', '', this.priceDescription(info.unitCents, info.pieces, product.unitLabel)));
    if (info.eligible && info.regularCents > info.unitCents) price.append(element('span', 'tth-wb__regular-price', `MSRP ${this.money(info.regularCents)}`));
    details.append(price);
    if (info.pricingMode === 'regular') details.append(element('p', 'tth-wo__note', 'Regular pricing; does not count toward wholesale minimums.'));
    const already = this.identities.find(line => line.variantId === variant.id)?.quantity || 0;
    const remaining = Math.min(MAX_QUANTITY, info.availableQuantity - already);
    details.append(element('p', 'tth-wq__stock', `${info.availableQuantity}${info.pieces > 1 ? ' sets' : ''} available${already ? ` · ${already} in your order` : ''}`));
    const quantity = element('label', 'tth-wq__quantity', info.pieces > 1 ? 'Number of sets' : 'Quantity');
    const input = quantityInput(1, `Quantity for ${shortTitle(product.title)}, ${variant.title}`);
    input.max = String(remaining); input.dataset.quickAddQuantity = ''; quantity.append(this.quickQuantityControl(input)); details.append(quantity);
    const add = button('tth-wq__add tth-wo__purchase', 'Add to wholesale order'); add.dataset.quickAddSubmit = '';
    add.disabled = !!this.purchasing || remaining < 1; details.append(add);
    nodes.push(details); body.replaceChildren(...nodes);
    const status = this.querySelector('[data-wholesale-quick-add-status]');
    if (status) status.textContent = '';
    this.renderQuickMinimum();
  }

  submitQuickAdd() {
    if (this.quickAddMode === 'pieces') {
      const quantity = integer(this.querySelector('[data-quick-add-piece-quantity]')?.value);
      if (quantity && this.setGroupQuantity(this.quickAddProduct, this.quickAddGroupKey, quantity)) { closeDialog(this.quickAddDialog); window.TTHRetailCart?.bounceCartBar(); return true; }
      const status = this.querySelector('[data-wholesale-quick-add-status]');
      if (status) status.textContent = quantity ? this.notice : 'Choose a whole number of pieces.';
      return false;
    }
    const product = this.products.get(this.quickAddProduct);
    const variant = product?.variants.find(item => item.id === this.quickAddVariant);
    const quantity = integer(this.querySelector('[data-quick-add-quantity]')?.value);
    if (product && variant && quantity && this.add(product, variant, quantity, { open: false })) {
      this.quickAddDialog._tthOpener = document.querySelector(`[data-quick-add-product="${product.id}"]`) || this.quickAddDialog._tthOpener;
      closeDialog(this.quickAddDialog);
      return true;
    }
    const status = this.querySelector('[data-wholesale-quick-add-status]');
    if (status) status.textContent = !quantity ? 'Choose a whole quantity within the available stock.' : this.notice || 'This option is unavailable. Please choose another option.';
    return false;
  }

  async recommendationRows(productId,intent){
    this.recommendationRequests||=new Map();const key=productId+':'+intent;
    if(!this.recommendationRequests.has(key))this.recommendationRequests.set(key,(async()=>{
      const url=new URL((window.Shopify?.routes?.root||'/')+'recommendations/products.json',location.origin);
      url.searchParams.set('product_id',productId);url.searchParams.set('intent',intent);url.searchParams.set('limit','10');
      try{const response=await fetch(url.href,{credentials:'same-origin'});if(!response.ok)throw Error();const data=await response.json();return Array.isArray(data.products)?data.products:[];}catch{this.recommendationRequests.delete(key);return [];}
    })());
    return this.recommendationRequests.get(key);
  }

  async ensureRecommendations(){
    if(this.recommendationsLoading||this.checkoutPage)return;
    const allIds=[...new Set(this.identities.map(line=>line.productId))],stamp=allIds.join(',');
    if(!stamp||this.recommendationStamp===stamp)return;
    this.recommendationsLoading=true;
    try{
      const seeds=recommendationSeeds(allIds.map(id=>this.products.get(id)||{id}));
      const groups=await Promise.all(seeds.map(async seed=>({family:seed.family,complementary:await this.recommendationRows(seed.id,'complementary'),related:await this.recommendationRows(seed.id,'related')})));
      const excluded=new Set(allIds);let selected=[];
      for(let attempt=0;attempt<20;attempt++){
        selected=mixCartSuggestions(groups,excluded);
        let rejected=false;
        for(const raw of selected){const productId=String(raw.id);let product=this.products.get(productId);
          if(!product){try{const url=new URL(raw.url||('/products/'+raw.handle),location.origin);if(url.origin!==location.origin)throw Error();url.search='';url.searchParams.set('view','tth-wholesale-data');const response=await fetch(url.href,{credentials:'same-origin'});if(response.ok)product=this.registerProduct((await response.json()).product);}catch{}}
          if(!product?.owned||product.vendor.toLowerCase()!=='the talavera hub'||!this.buyableVariants(product).some(variant=>this.variantInfo(product,variant).spendEligible)){excluded.add(productId);rejected=true;}
        }
        if(!rejected)break;
      }
      this.recommendedProductIds=selected.map(product=>String(product.id)).filter(id=>!excluded.has(id));this.recommendationStamp=stamp;

    }finally{this.recommendationsLoading=false;this.renderRecommendations();}
  }

  recommendationProducts(){
    const inOrder=new Set(this.identities.map(line=>line.productId));
    return (this.recommendedProductIds||[]).map(productId=>this.products.get(productId)).filter(product=>product&&!inOrder.has(product.id)&&this.buyableVariants(product).some(variant=>this.variantInfo(product,variant).spendEligible)).slice(0,4);
  }

  renderRecommendations() {
    const container = this.querySelector('[data-order-recommendations]');
    if (!container) return;
    const products = this.identities.length && !this.checkoutPage ? this.recommendationProducts() : [];
    if(this.dialog?.open&&!this.recommendationsLoading)void this.ensureRecommendations();
    container.replaceChildren(...products.map(product => this.renderCard(product, {})));
    const section = this.querySelector('[data-order-recommendations-section]');
    if (section) section.hidden = products.length === 0;
  }

  discoverGuides() {
    document.querySelectorAll('[data-wholesale-product]').forEach(root => {
      if (this.guides.has(root)) return;
      const raw = parseJSON(root.querySelector('[data-wholesale-product-json]')?.textContent || '');
      const product = this.registerProduct(raw?.product || raw, { inline: true });
      if (!product) return;
      const state = { productId: product.id, stamp: '', secondary: null, buttonContainer: null };
      this.guides.set(root, state);
      void this.loadCostPrices([product]).then(()=>this.refreshGuide(root,state,true));
      const signal = this.controller.signal;
      const dialog = root.querySelector('[data-wholesale-guide-dialog]');
      dialog?.addEventListener('close', () => restoreFocus(dialog), { signal });
      root.addEventListener('click', event => {
        if (event.target.closest('[data-wholesale-guide-open]')) {
          const selected = this.selectedProductVariant(root, state.productId);
          const guideRetailQuantity = integer(formField(selected?.form, 'quantity')?.value) || 1;
          const selectedGroup = selected && this.groupForVariant(selected.product, selected.variant.id);
          const existingPieces = selectedGroup?.mode === 'pieces' ? this.groupQuantity(selectedGroup) : 0;
          const guidePieces = existingPieces || (selected?.variant.pieces ? guideRetailQuantity * selected.variant.pieces : null);
          if (selected && this.quickAddDialog && this.quickAdd(product.id, event.target.closest('[data-wholesale-guide-open]'), { force: true, selectedVariantId: selected.variant.id, defaultPieces: guidePieces })) {
            if (this.quickAddMode === 'variants') {
              const quantity = this.querySelector('[data-quick-add-quantity]');
              if (quantity) { quantity.value = String(guideRetailQuantity); this.renderQuickMinimum(); }
            }
            return;
          }
          this.refreshGuide(root, state, true);
          const form = this.productForm(root, product.id);
          const input = root.querySelector('[data-wholesale-product-quantity]');
          const retailQuantity = integer(formField(form, 'quantity')?.value);
          if (input && retailQuantity && retailQuantity <= MAX_QUANTITY) input.value = String(retailQuantity);
          showDialog(dialog, event.target.closest('[data-wholesale-guide-open]'));
        }
        if (event.target.closest('[data-wholesale-guide-close]')) closeDialog(dialog);
        if (event.target.closest('[data-wholesale-product-add]')) {
          const selected = this.selectedProductVariant(root, state.productId);
          const quantity = integer(root.querySelector('[data-wholesale-product-quantity]')?.value);
          const status = root.querySelector('[data-wholesale-product-status]');
          if (selected && quantity && quantity <= MAX_QUANTITY) {
            if (this.add(selected.product, selected.variant, quantity)) closeDialog(dialog);
            else if (status) status.textContent = this.notice;
          } else if (status) status.textContent = 'Choose an available option and enter a whole quantity from 1 to 9,999.';
        }
      }, { signal });
      this.refreshGuide(root, state, true);
    });
    this.startGuideWatcher();
  }

  startGuideWatcher() {
    if (this.guideTimer || !this.guides.size) return;
    this.guideTimer = window.setInterval(() => {
      this.refreshGuides();
      if (!this.guides.size) { window.clearInterval(this.guideTimer); this.guideTimer = null; }
    }, 700);
  }

  productForm(root, productId) {
    const scopes = [root.closest('product-info'), root.closest('.product'), root.closest('.shopify-section'), document.querySelector('main')].filter(Boolean);
    for (const scope of scopes) {
      const forms = [...scope.querySelectorAll('form[action*="/cart/add"]')].filter(form => formField(form, 'id'));
      const matching = forms.find(form => {
        const variantId = id(formField(form, 'id')?.value);
        return this.variants.get(variantId)?.product.id === productId;
      });
      if (matching) return matching;
    }
    return null;
  }

  selectedProductVariant(root, productId) {
    const product = this.products.get(productId);
    if (!product) return null;
    const form = this.productForm(root, productId);
    const variantId = id(formField(form, 'id')?.value);
    const variant = variantId ? product.variants.find(item => item.id === variantId) : product.variants.length === 1 ? product.variants[0] : null;
    return variant ? { product, variant, form } : null;
  }

  refreshGuides(force = false) {
    this.guides.forEach((state, root) => {
      if (!root.isConnected) { state.secondary?.remove(); this.guides.delete(root); return; }
      this.refreshGuide(root, state, force);
    });
  }

  refreshGuide(root, state, force = false) {
    const selected = this.selectedProductVariant(root, state.productId);
    const product = this.products.get(state.productId);
    if (!product) return;
    const info = selected ? this.variantInfo(product, selected.variant) : null;
    const stamp = `${selected?.variant.id || ''}:${selected?.variant.price}:${selected?.variant.compareAtPrice}:${selected?.variant.pieces}:${formField(selected?.form, 'quantity')?.value}:${info?.buyable}:${info?.availableQuantity}:${this.identities.length > 0}`;
    if (!force && state.stamp === stamp && (!state.secondary || state.secondary.isConnected)) return;
    state.stamp = stamp;
    const trigger = root.querySelector('[data-wholesale-guide-open]');
    // Keep regular-only products free of a misleading wholesale-price entry point.
    const owned = product.owned === true && product.vendor.toLowerCase() === 'the talavera hub';
    const hasEligibleOption = owned && product.variants.some(variant => this.variantInfo(product, variant).spendEligible);
    if (trigger) trigger.hidden = !hasEligibleOption;
    if (trigger && this.quickAddDialog?.id) trigger.setAttribute('aria-controls', this.quickAddDialog.id);
    const pricingMode = owned ? info?.pricingMode || '' : '';
    root.dataset.wholesalePricingMode = pricingMode;
    const request = root.querySelector('[data-wholesale-custom-quantity-link]');
    if (request) request.hidden = true;
    root.dispatchEvent(new CustomEvent('tth:wholesale-variant-state', { bubbles: true, detail: { variantId: selected?.variant.id || '', eligible: owned && !!info?.eligible, buyable: !!info?.buyable, pricingMode } }));
    const details = root.querySelector('[data-wholesale-guide-details]');
    if (details) {
      const nodes = [];
      if (selected) {
        if (selected.variant.title !== 'Default Title') nodes.push(element('p', '', selected.variant.title));
        if (selected.variant.sku) nodes.push(element('p', 'tth-wo__note', `SKU: ${selected.variant.sku}`));
        if (info.spendEligible) {
          const price = element('p', 'tth-wholesale-product__price');
          if (info.regularCents > info.unitCents) price.append(element('del', 'tth-wholesale-product__regular', this.priceDescription(info.regularCents, info.pieces, product.unitLabel)));
          price.append(element('strong', '', this.priceDescription(info.unitCents, info.pieces, product.unitLabel)));
          nodes.push(price);
        } else nodes.push(element('p', 'tth-wo__note', `${this.priceDescription(info.unitCents, info.pieces, product.unitLabel)} · regular pricing. Does not count toward wholesale minimums.`));
        if (info.availableQuantity > 0) nodes.push(element('p', 'tth-wo__note', `${info.availableQuantity} available${info.pieces > 1 ? ' sets' : ''}.`));
        if (info.reason) nodes.push(element('p', 'tth-wo__warning', info.reason));
      } else nodes.push(element('p', '', 'Choose a product option to see its wholesale price.'));
      details.replaceChildren(...nodes);
    }
    const add = root.querySelector('[data-wholesale-product-add]');
    if (add) add.disabled = !info?.buyable || !!this.purchasing;
    const quantity = root.querySelector('[data-wholesale-product-quantity]');
    if (quantity) {
      quantity.setAttribute('aria-label', info?.pieces > 1 ? `Number of sets, ${info.pieces} pieces per set` : 'Quantity');
      const inOrder = this.identities.find(line => line.variantId === selected?.variant.id)?.quantity || 0;
      quantity.max = String(Math.max(0, Math.min(MAX_QUANTITY, (info?.availableQuantity || 0) - inOrder)));
    }
    this.refreshSecondary(root, state, selected, info);
  }

  refreshSecondary(root, state, selected, info) {
    const buttons = selected?.form?.querySelector('.product-form__buttons');
    // This theme nests quantity + retail ATC in .tth-atc-row. Splitting its outer
    // wrapper would put accelerated payment beside ATC and wholesale beside terms.
    const atcRow = buttons?.querySelector('.tth-atc-row');
    const container = atcRow || buttons;
    const wanted = this.identities.length > 0 && info?.buyable && container;
    if (!wanted) {
      state.secondary?.remove();
      state.secondary = null;
      if (state.buttonContainer && !state.buttonContainer.querySelector('.tth-wholesale-secondary')) state.buttonContainer.classList.remove('tth-wholesale-split', 'tth-wholesale-split--with-quantity');
      state.buttonContainer = null;
      return;
    }
    if (!state.secondary || !state.secondary.isConnected || state.buttonContainer !== container) {
      state.secondary?.remove();
      if (state.buttonContainer && !state.buttonContainer.querySelector('.tth-wholesale-secondary')) state.buttonContainer.classList.remove('tth-wholesale-split', 'tth-wholesale-split--with-quantity');
      const secondary = button('button button--secondary tth-wholesale-secondary', 'Add to wholesale order');
      secondary.addEventListener('click', async () => {
        const current = this.selectedProductVariant(root, state.productId);
        if(current)await this.loadCostPrices([current.product]);
        const quantity = integer(formField(current?.form, 'quantity')?.value || '1');
        const group = current && this.groupForVariant(current.product, current.variant.id);
        if (current && quantity && quantity <= MAX_QUANTITY && group?.mode === 'pieces') { if(this.setGroupQuantity(current.product.id, group.key, this.groupQuantity(group) + quantity * current.variant.pieces)){state.addedUntil=Date.now()+2500;this.refreshSecondary(root,state,current,this.variantInfo(current.product,current.variant));} }
        else if (current && quantity && quantity <= MAX_QUANTITY) { if(this.add(current.product, current.variant, quantity,{open:false})){state.addedUntil=Date.now()+2500;this.refreshSecondary(root,state,current,this.variantInfo(current.product,current.variant));} }
        else this.status('Choose an available option and a whole quantity from 1 to 9,999.');
      }, { signal: this.controller.signal });
      container.append(secondary);
      container.classList.add('tth-wholesale-split');
      if (atcRow?.querySelector('.quantity')) container.classList.add('tth-wholesale-split--with-quantity');
      state.secondary = secondary;
      state.buttonContainer = container;
    }
    const group = selected && this.groupForVariant(selected.product, selected.variant.id);
    const quantity = integer(formField(selected?.form, 'quantity')?.value || '1') || 1;
    const pieces = quantity * (selected?.variant.pieces || 1);
    const resultingPieces = group?.mode === 'pieces' ? this.groupQuantity(group) + pieces : 0;
    const plan = group?.mode === 'pieces' ? this.planForGroup(group, resultingPieces) : null;
    const floor = this.quantityPriceFloor(selected.product);
    const price = !plan?.ok && group?.mode === 'pieces' ? 'Quantity needs review' : !info?.eligible && selected.variant.pieces > 1 ? `${this.money(info.unitCents * quantity)} / selected set` : floor ? `From ${this.money(floor.plan.unitCents)} / ${selected.product.unitLabel || 'piece'}` : `${this.money(info.unitCents)} / ${selected.product.unitLabel || 'piece'}`;
    state.secondary.replaceChildren(element('span', 'tth-wholesale-secondary__label', state.addedUntil>Date.now()?'Added to wholesale order':'Add to wholesale order'), element('span', 'tth-wholesale-secondary__price', price));
    state.secondary.disabled = !!this.purchasing || group?.mode === 'pieces' && !plan?.ok;
  }
}

class TthWholesaleBuilder extends BrowserElement {
  connectedCallback() { manager?.attachBuilder(this); }
  disconnectedCallback() { manager?.builders.delete(this); }
}

if (typeof window !== 'undefined' && window.customElements) {
  if (!window.customElements.get('tth-wholesale-order')) window.customElements.define('tth-wholesale-order', TthWholesaleOrder);
  if (!window.customElements.get('tth-wholesale-builder')) window.customElements.define('tth-wholesale-builder', TthWholesaleBuilder);
}
